import type {GuideData} from './seoTypes'

// ── App Store & Google Play screenshot sizes ────────────────────────

const appStoreSizes: GuideData = {
  slug: 'app-store-screenshot-sizes',
  canonicalPath: '/guides/app-store-screenshot-sizes',
  title: 'App Store Screenshot Sizes & Requirements 2026 | Ecranify',
  description:
    'App Store screenshot sizes for 2026: the required 6.3″ iPhone set (1206 × 2622), optional 6.9″, iPad 13″, iPhone Duo and Google Play screenshot dimensions.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'App Store screenshot sizes and requirements (2026)',
  intro: [
    'The App Store screenshot size you must provide in 2026 is the 6.3″ iPhone set, which App Store Connect calls “iPhone with Dynamic Island (medium display)”: 1206 × 2622 or 1179 × 2556 pixels in portrait. If your app runs on iPad, you also need a 13″ iPad set at 2064 × 2752 or 2048 × 2732 pixels.',
    'That is a change. For the last few years the 6.9″ iPhone set (1320 × 2868) was the one mandatory slot, and many size guides still say so. This page lists every size App Store Connect and Google Play accept today, which ones are required, and how to export them, checked against Apple’s and Google’s own help pages in October 2026.',
  ],
  updated: 'October 2026',
  readingTime: '8 min read',
  sections: [
    {
      heading: 'Which App Store screenshot sizes are required in 2026',
      body: [
        'Apple’s screenshot specifications list two required display sizes: at least one screenshot for iPhone with Dynamic Island (medium display), and at least one for the iPad 13″ display if your app runs on iPad. Every other size is optional, and App Store Connect fills a missing size with scaled versions of a larger one. Mac, Apple TV, Apple Vision Pro and Apple Watch apps have their own required sizes.',
        'One detail needs care. The entry for the 6.5″ class (iPhone with Face ID, large display) still says it is required when you provide no 6.9″ set. The safe approach is therefore to upload a 6.3″ set and a 6.9″ set. Both stores check pixel dimensions on upload, and an image that matches no accepted size [is rejected before review](/guides/app-store-screenshot-rejection-reasons).',
      ],
      table: {
        caption: 'Required App Store screenshot sizes (October 2026)',
        headers: ['Display', 'App Store Connect name', 'Portrait (px)', 'Required?'],
        rows: [
          [
            'iPhone 6.3″',
            'iPhone with Dynamic Island (medium display)',
            '1206 × 2622 or 1179 × 2556',
            'Yes, if the app runs on iPhone',
          ],
          ['iPad 13″', 'iPad 13″ display', '2064 × 2752 or 2048 × 2732', 'Yes, if the app runs on iPad'],
        ],
      },
    },
    {
      heading: 'All iPhone screenshot sizes App Store Connect accepts',
      body: [
        'App Store Connect now names iPhone sizes by device type instead of by screen diagonal; the inch values below are the usual shorthand. Each size is accepted in portrait or landscape (swap the two numbers), and you can upload up to 10 screenshots per device size and localization.',
        'If your interface looks the same on every iPhone, Apple only needs the highest required resolution and scales it down for smaller devices. Upload device-specific sets only where the layout really differs. On iOS 27 and later the product page and search results can also show separate creative assets, covered in our [App Store header and search results asset guide](/guides/app-store-header-search-results-assets).',
      ],
      table: {
        caption: 'iPhone screenshot sizes (portrait)',
        headers: ['Display', 'Portrait (px)', 'Example devices', 'Status'],
        rows: [
          [
            '6.9″ (Dynamic Island, large)',
            '1320 × 2868, 1290 × 2796, 1260 × 2736',
            'iPhone 18 Pro Max, 17 Pro Max, iPhone Air, 16 Pro Max, 16 Plus',
            'Optional',
          ],
          [
            '6.3″ (Dynamic Island, medium)',
            '1206 × 2622, 1179 × 2556',
            'iPhone 18 Pro, 17 Pro, 17, 16 Pro, 16, 15 Pro, 15',
            'Required',
          ],
          [
            '6.5″ (Face ID, large)',
            '1284 × 2778, 1242 × 2688',
            'iPhone 14 Plus, 13 Pro Max, 12 Pro Max, 11 Pro Max, 11, XR',
            'Required if no 6.9″ set',
          ],
          [
            '6.1″ (Face ID, medium)',
            '1170 × 2532, 1125 × 2436, 1080 × 2340',
            'iPhone 17e, 16e, 14, 13, 12, X',
            'Optional',
          ],
          ['5.5″ (Home Button, large)', '1242 × 2208', 'iPhone 8 Plus, 7 Plus, 6s Plus', 'Optional'],
          ['4.7″ (Home Button, medium)', '750 × 1334', 'iPhone SE (2nd and 3rd gen), 8, 7', 'Optional'],
        ],
      },
    },
    {
      heading: 'iPhone Duo screenshot sizes',
      body: [
        'Apple’s foldable iPhone Duo has two displays, and App Store Connect accepts a set for each: 1398 × 2034 pixels for the outer display and 2007 × 2853 pixels for the inner display (portrait). Both are optional today.',
        'Apple’s specification adds that, starting in April 2027, iPhone Duo screenshots will be required for any app built with the iOS 27.1 SDK or later. If you plan to ship with that SDK, design the Duo sets now rather than in the week of the deadline.',
      ],
    },
    {
      heading: 'iPad screenshot sizes',
      body: [
        'Only the 13″ size is required for iPad apps; the smaller iPad classes fall back to scaled 13″ (or 12.9″) screenshots when you don’t upload them.',
      ],
      table: {
        caption: 'iPad screenshot sizes (portrait)',
        headers: ['Display', 'Portrait (px)', 'Example devices', 'Status'],
        rows: [
          ['13″', '2064 × 2752, 2048 × 2732', 'iPad Pro (M5, M4), iPad Air 13″ (M4, M3, M2)', 'Required for iPad apps'],
          ['12.9″', '2048 × 2732', 'iPad Pro (2nd gen)', 'Optional'],
          [
            '11″',
            '1668 × 2420, 1668 × 2388, 1640 × 2360, 1488 × 2266',
            'iPad Pro 11″, iPad Air 11″, iPad (A16), iPad mini (A17 Pro)',
            'Optional',
          ],
          ['10.5″', '1668 × 2224', 'iPad (7th–9th gen), iPad Air (3rd gen)', 'Optional'],
        ],
      },
    },
    {
      heading: 'File format, count and orientation rules',
      body: ['The pixel size is only one of the checks. Before you upload to App Store Connect:'],
      bullets: [
        'Use .png, .jpg or .jpeg files with no alpha channel or transparency. Export opaque images even if your design tool adds transparency by default.',
        'Upload between 1 and 10 screenshots per device size, per localization.',
        'Pick one orientation per set: every accepted size works in portrait or landscape.',
        'Design at the exact target resolution and export 1:1; resizing a finished file by hand is the most common cause of a size mismatch.',
        'Keep important content away from the edges so nothing is lost when Apple scales a set down for smaller devices.',
      ],
    },
    {
      heading: 'Google Play screenshot sizes',
      body: [
        'Google Play is flexible on exact pixels but strict on proportions. Each screenshot must be a JPEG or 24-bit PNG with no alpha channel, each side between 320 and 3,840 px, and the long side no more than twice the short side. You can add up to 8 screenshots per device type and need at least two to publish; the exact sizes per device type are in our [Google Play screenshot size guide](/guides/google-play-screenshot-requirements).',
        'To be eligible for Google Play’s recommendations, apps should provide at least four screenshots of at least 1080 px: 1080 × 1920 in portrait or 1920 × 1080 in landscape. For tablets and Chromebooks, Google asks for 9:16 or 16:9 images between 1,080 and 7,680 px and at least four screenshots. The 512 × 512 store icon has its own rules, covered in our [App Store and Google Play icon requirements guide](/guides/app-store-icon-guidelines).',
        'Every listing also needs a 1024 × 500 feature graphic; our [Google Play feature graphic guide](/guides/google-play-feature-graphic) covers its size and content rules.',
      ],
      table: {
        caption: 'Google Play graphic asset sizes',
        headers: ['Asset', 'Size (px)', 'Format', 'Notes'],
        rows: [
          [
            'Phone screenshot',
            '1080 × 1920 recommended (320–3,840 per side)',
            'JPEG / 24-bit PNG, no alpha',
            'Up to 8; at least 4 for recommendations',
          ],
          [
            'Tablet / Chromebook screenshot',
            '9:16 or 16:9, 1,080–7,680',
            'JPEG / 24-bit PNG, no alpha',
            'At least 4, up to 8',
          ],
          ['App icon', '512 × 512', '32-bit PNG, max 1024 KB', 'Required'],
          ['Feature graphic', '1024 × 500', 'JPEG / 24-bit PNG, no alpha', 'Required'],
        ],
      },
    },
    {
      heading: 'How to export these sizes in Ecranify',
      body: [
        'Ecranify has export presets for the iPhone 6.9″ (1320 × 2868), 6.7″ (1290 × 2796), 6.5″ (1284 × 2778) and 5.5″ slots, the iPad 13″ (2064 × 2752) and 11″ (1668 × 2388) slots, and Google Play phone, tablet and feature graphic sizes. For the newly required 6.3″ iPhone set, choose a custom export resolution of 1206 × 2622. Pick a device, drop in your screenshot, set the size and export, all in the browser, free to start and pay only per export.',
        'For [connected, panoramic screenshot sets](/guides/panoramic-app-store-screenshots), Ecranify’s carousel export renders one continuous scene and slices it into 2–5 store-ready images at the size you chose.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/iphone-6-9.jpg',
      alt: 'App Store screenshot made in Ecranify on an iPhone 6.9″ device (1320×2868)',
      caption: 'iPhone 6.9″ (1320×2868), the optional large-iPhone App Store size.',
    },
    {
      src: '/marketing/examples/ipad-13.jpg',
      alt: 'App Store screenshot made in Ecranify on an iPad 13″ device (2064×2752)',
      caption: 'iPad 13″ (2064×2752), required if your app supports iPad.',
    },
    {
      src: '/marketing/examples/galaxy-tab.jpg',
      alt: 'Google Play tablet screenshot made in Ecranify on a Galaxy Tab',
      caption: 'Google Play tablet screenshot, exported at store size.',
    },
    {
      src: '/marketing/examples/carousel.jpg',
      alt: 'A three-frame App Store carousel screenshot strip made in Ecranify',
      caption: 'Carousel export: one connected strip, sliced into store-ready frames.',
      wide: true,
    },
  ],
  faq: [
    {
      q: 'What iPhone screenshot size is required in 2026?',
      a: 'The 6.3-inch class, called iPhone with Dynamic Island (medium display) in App Store Connect: 1206 × 2622 or 1179 × 2556 pixels in portrait. Apple scales it down for smaller iPhones that have no set of their own.',
    },
    {
      q: 'Is the 6.9-inch screenshot size still required?',
      a: 'It is now optional, and 1320 × 2868, 1290 × 2796 and 1260 × 2736 are still accepted. Apple’s specification marks the 6.5-inch set as required when no 6.9-inch set is provided, so uploading both a 6.3-inch and a 6.9-inch set is the safe choice.',
    },
    {
      q: 'What is the iPad 13-inch screenshot size?',
      a: 'The iPad 13-inch display uses 2064 × 2752 or 2048 × 2732 pixels in portrait (swap them for landscape). It is required only if your app runs on iPad.',
    },
    {
      q: 'What size are Google Play phone screenshots?',
      a: 'Any JPEG or 24-bit PNG without alpha, with each side between 320 and 3,840 pixels and the long side at most twice the short side. 1080 × 1920 is the recommended portrait size, and four or more such screenshots make an app eligible for recommendations.',
    },
    {
      q: 'Can App Store screenshots have transparency?',
      a: 'No. App Store Connect does not accept images with an alpha channel or transparency, and Google Play also requires images without alpha. Export opaque PNG or JPEG files.',
    },
    {
      q: 'Do I have to make each size separately?',
      a: 'No. If the layout is the same on every device, upload the required sizes and let the stores scale them. In Ecranify you design once and export each size from the same project.',
    },
  ],
  cta: {
    title: 'Export every store size in minutes',
    body: 'Ecranify has presets for iPhone, iPad and Google Play built in, plus custom export sizes. Design once, export store-ready images, free to start.',
  },
}

// ── Google Play feature graphic ─────────────────────────────────────

const playFeatureGraphic: GuideData = {
  slug: 'google-play-feature-graphic',
  canonicalPath: '/guides/google-play-feature-graphic',
  title: 'Google Play Feature Graphic: Size & Rules (2026) | Ecranify',
  description:
    'The Google Play feature graphic is 1024 × 500 px, JPEG or 24-bit PNG with no alpha. See where it appears, cutoff zones, what Google says to avoid, and a layout.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'Google Play feature graphic: size, rules & template',
  intro: [
    'A Google Play feature graphic must be exactly 1024 × 500 px, saved as a JPEG or a 24-bit PNG with no alpha channel, and you need one before your store listing can be published. Google shows it as the cover of your preview video and in large-format app collections, so it is often the first image a shopper sees.',
    'This guide covers the full spec, where the feature graphic appears, the cutoff zones to keep clear, the content Google’s own guidelines ask you to leave out, a simple layout you can reuse, and how to test variants in Play Console. The specs and rules below were checked against Google’s Play Console Help in October 2026.',
  ],
  updated: 'October 2026',
  readingTime: '7 min read',
  sections: [
    {
      heading: 'What the feature graphic is and where it appears',
      body: [
        'The feature graphic is a wide, landscape banner that belongs to your [store listing’s graphic assets](/guides/app-store-assets), next to the app icon and screenshots. Google lists several places where it is displayed: as the cover image of your preview video when you have one, in collections that show apps in a large format (including ads), and, for games, in groups of recommended games.',
        'Because it often appears before anyone opens your listing, the banner works like a poster for the app. It has to read quickly, at phone size, next to other apps’ banners.',
      ],
    },
    {
      heading: 'Google Play feature graphic size and format',
      body: [
        'The technical requirements are short. Google marks the first three rows below as required to publish; alt text is highly recommended. Google’s current help page does not state a separate file-size limit for the feature graphic.',
      ],
      table: {
        caption: 'Google Play feature graphic specifications',
        headers: ['Property', 'Requirement'],
        rows: [
          ['Dimensions', '1024 × 500 px (landscape)'],
          ['Format', 'JPEG or 24-bit PNG'],
          ['Transparency', 'Not allowed (no alpha channel)'],
          ['Required to publish', 'Yes'],
          ['Alt text', 'Highly recommended, 140 characters or less'],
        ],
      },
    },
    {
      heading: 'Cutoff zones: keep the focal point in the center',
      body: [
        'Depending on the surface, Google Play may crop parts of the banner. Google’s guidelines show these cutoff zones as red areas in an example image but publish no pixel values, so the safe approach is relative rather than exact.',
      ],
      bullets: [
        'Keep the brand logo, app name, main slogan and main UI out of the outer areas of the banner.',
        'Put the most prominent visual and the focal point toward the center.',
        'Use the edges for background only — color, texture or a scene that can be cropped without losing meaning.',
        'Avoid fine details; they disappear when the banner is shown small.',
        'Prefer vibrant colors and avoid pure white or dark gray, which blend into Google Play’s own backgrounds.',
      ],
    },
    {
      heading: 'What Google asks you to leave out',
      body: [
        'The Play Console guidelines for preview assets list content to avoid in the feature graphic. Google marks these points as highly recommended rather than required, but they are worth checking a design against before upload:',
      ],
      bullets: [
        'Performance or ranking claims, testimonials, awards, price or promotional information — words like “Best”, “#1”, “Top”, “New”, “Free”, “Discount”, “Sale” or “Million Downloads”.',
        'Calls to action such as “Download now”, “Install now”, “Play now” or “Try now”.',
        'Time-sensitive content that quickly goes out of date.',
        'Device imagery — Google notes that it can become obsolete quickly or alienate some users.',
        'Google Play or other store badges, and third-party trademarked characters.',
        'Prominent branding that simply repeats your app icon.',
      ],
    },
    {
      heading: 'A simple Google Play feature graphic template',
      body: [
        'Most strong feature graphics follow the same layout. Start with a full-bleed background in your brand colors that runs to every edge. Place one large focal visual near the center: a key piece of your interface shown big, a character or scene from your game, or a bold visual metaphor for what the app does. Add, at most, a short line of copy or the app name, set large enough to read on a phone.',
        'Build the banner once and swap only the copy and the focal visual for each market. Google recommends localizing the graphic and its branding text; the same one-scene, many-languages approach is described in our [guide to localized App Store screenshots](/guides/localized-app-store-screenshots).',
      ],
    },
    {
      heading: 'Test feature graphic variants in Play Console',
      body: [
        'You do not have to guess which banner works. Play Console store listing experiments can include variants of your icon, feature graphic and screenshots. Each experiment tests up to 2 variants against your current listing, and an app can run one default graphics experiment or up to five localized experiments at the same time. Our [App Store and Google Play screenshot A/B testing guide](/guides/app-store-screenshot-ab-testing) covers how long to run a test and how to read the result.',
        'Custom store listings are another option: you can create up to 50 of them and customize the app name, icon, descriptions and graphic assets for a specific audience.',
      ],
    },
    {
      heading: 'How to make a feature graphic in Ecranify',
      body: [
        'Ecranify has a Google Play feature graphic export preset at exactly 1024 × 500, next to the Play phone and tablet presets, so the canvas already has the right aspect while you design. Set a gradient or image background, add 2D or 3D headline text and frame the scene with the camera; dynamic shadows adapt to the wide format.',
        'Ecranify is built around 3D device mockups, and Google’s guidelines list device imagery among the things to avoid in a feature graphic. If you follow that advice strictly, zoom the camera in so your app’s interface, not the hardware, becomes the focal point, or keep the device small and secondary. You can try the layout in the [free Ecranify editor](/free) and pay only when you export finished images.',
        'The phone and tablet screenshots in the same listing follow separate rules, covered in our [Google Play screenshot requirements guide](/guides/google-play-screenshot-requirements).',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/feature-graphic.jpg',
      alt: 'A 1024×500 Google Play feature graphic with a 3D device mockup made in Ecranify',
      caption: 'A 1024×500 Google Play feature graphic composed in Ecranify.',
    },
  ],
  faq: [
    {
      q: 'What size is the Google Play feature graphic?',
      a: 'Exactly 1024 × 500 pixels, in landscape orientation, as a JPEG or a 24-bit PNG with no alpha channel.',
    },
    {
      q: 'Is the feature graphic required?',
      a: 'Yes. Google Play requires a feature graphic before you can publish your store listing.',
    },
    {
      q: 'Can the feature graphic have transparency?',
      a: 'No. PNG files must be 24-bit with no alpha channel, so upload an opaque image that fills the whole 1024 × 500 frame.',
    },
    {
      q: 'Where does the feature graphic appear?',
      a: 'Google lists the cover image of your preview video, large-format app collections including ads, and, for games, groups of recommended games. Parts of it can be cut off depending on the format, so keep key elements toward the center.',
    },
    {
      q: 'Can I put a phone mockup in my feature graphic?',
      a: 'It is not a technical requirement, but Google’s guidelines recommend avoiding device imagery because it can date quickly or alienate some users. A large, cropped view of your interface usually follows the guidelines better.',
    },
    {
      q: 'Can I A/B test my feature graphic?',
      a: 'Yes. Play Console store listing experiments can include feature graphic variants, with up to 2 variants tested against your current listing per experiment.',
    },
  ],
  cta: {
    title: 'Make a 1024×500 feature graphic free',
    body: 'Compose a Google Play feature graphic with a gradient or image background and bold headline text in your browser. Free to start — pay only when you export.',
  },
}

// ── Figma to App Store Screenshots ──────────────────────────────────

const figmaWorkflow: GuideData = {
  slug: 'figma-app-store-screenshots',
  canonicalPath: '/guides/figma-app-store-screenshots',
  title: 'Figma App Store Screenshots: Sizes, Export & 3D | Ecranify',
  description:
    'How to make App Store and Google Play screenshots in Figma: exact frame sizes, export settings that avoid alpha-channel rejections, and a 3D mockup workflow.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'Figma App Store screenshots: frame sizes, export settings and a 3D workflow',
  intro: [
    'To make App Store screenshots in Figma, set each frame to the exact pixel size the store accepts (1320 × 2868 px for the 6.9″ iPhone slot, 2064 × 2752 px for the 13″ iPad slot), design at that size, and export at 1x as JPG or as a PNG with the alpha channel removed. Figma always writes PNGs with an alpha channel, and Google Play explicitly rejects screenshots with alpha, so the export format matters as much as the layout.',
    'This guide covers the frame sizes for both stores, the export settings that upload cleanly, how to structure a Figma file so a whole screenshot set stays editable, and where a 3D device mockup tool fits when you want angled devices, depth and lighting that Figma’s flat frames can’t produce.',
  ],
  updated: 'October 2026',
  readingTime: '8 min read',
  sections: [
    {
      heading: 'Figma frame sizes for App Store and Google Play screenshots',
      body: [
        'Design each screenshot in a frame that is already the final pixel size. When the frame matches the store size exactly, a 1x export produces an upload-ready file with no resampling, and text stays as sharp as you drew it. Apple accepts JPEG or PNG and between 1 and 10 screenshots per device size and localization.',
        'For iPhone you provide the 6.9″ set (or the 6.5″ set as a fallback); App Store Connect scales them for smaller iPhones. The 13″ iPad set is required only if your app runs on iPad. Google Play is range-based rather than fixed: any size from 320 to 3840 px works as long as the long side is no more than twice the short side. The full list of accepted variants is in our [App Store and Google Play screenshot sizes guide](/guides/app-store-screenshot-sizes).',
      ],
      table: {
        caption: 'Figma frame sizes that export straight to store-ready files (portrait)',
        headers: ['Store slot', 'Figma frame (export at 1x)', 'Also accepted', 'When you need it'],
        rows: [
          [
            'App Store — iPhone 6.9″',
            '1320 × 2868 px',
            '1290 × 2796, 1260 × 2736 px',
            'Apps that run on iPhone (or provide 6.5″)',
          ],
          ['App Store — iPhone 6.5″', '1284 × 2778 px', '1242 × 2688 px', 'Only if you skip the 6.9″ set'],
          ['App Store — iPad 13″', '2064 × 2752 px', '2048 × 2732 px', 'Apps that run on iPad'],
          [
            'Google Play — phone',
            '1080 × 1920 px',
            '320–3840 px, long side ≤ 2× short side',
            'Two or more screenshots across device types to publish',
          ],
          [
            'Google Play — 7″/10″ tablet',
            '1080 × 1920 px',
            '9:16 or 16:9, 1080–7680 px',
            'Tablet listing: at least 4 screenshots',
          ],
        ],
      },
    },
    {
      heading: 'Export settings that pass the upload check',
      body: [
        'Figma exports PNG, JPG, SVG and PDF, with scale presets from 0.5x to 4x or a fixed width or height. According to Figma’s own documentation, it is not possible to export a PNG without an alpha value: every PNG is a 32-bit RGBA file, even when the frame has a solid background. Google Play requires JPEG or 24-bit PNG with no alpha, and developers regularly report [App Store Connect refusing Figma PNGs](/guides/app-store-screenshot-rejection-reasons) with an “image can’t contain alpha channels or transparencies” error.',
        'The simplest fix is to export JPG. Figma exports JPGs at High quality by default, which keeps UI text readable; don’t lower it for screenshots. If you need PNG, flatten the alpha channel after export (for example by re-saving the file with alpha turned off in macOS Preview) before uploading. To turn a PNG export into a JPEG without leaving the browser, see our [free image converter and video-to-GIF tools](/guides/free-image-converter-video-to-gif).',
      ],
      bullets: [
        'Scale: 1x when the frame is already at store size. Exporting a 440-wide frame at 3x also works, but only if the result lands on an accepted size.',
        'Format: JPG (High) for store uploads; PNG only if you strip the alpha channel afterwards.',
        'Background: fill the whole frame with an opaque color or image so no edge pixel is transparent.',
        'Naming: prefix frames with their order (01-onboarding, 02-search) and add the locale as a suffix, so a batch export sorts correctly.',
      ],
    },
    {
      heading: 'Structure the Figma file so the set stays editable',
      body: [
        'A screenshot set is usually 5–10 frames per device size, multiplied by every language you support. Rebuilding frames by hand for each change doesn’t scale, so build one master component (background, headline block, device area) and make every screenshot an instance of it. A change to the headline style or background then propagates to the whole set.',
        'Put headlines in auto layout containers so longer translations grow the block instead of overflowing it, and use variants or variables for light/dark themes and per-language copy. If you ship in several languages, the planning side of that — which markets to localize first and what to translate — is covered in our [guide to localized App Store screenshots](/guides/localized-app-store-screenshots).',
      ],
    },
    {
      heading: 'Where Figma stops: angled 3D devices',
      body: [
        'Figma is excellent for UI layout, components and copy, but its frames are flat. Realistic device rotation, depth, reflections and shadows are hard to fake with 2D mockup frames, and every change of angle means re-editing the mockup by hand.',
        'A practical split is to keep designing the app screens in Figma and move the presentation step — device, camera angle, lighting, background — into a 3D tool. Ecranify renders real 3D device models in the browser, so the screen you exported from Figma becomes a texture on the device and you choose the angle with the mouse instead of redrawing it.',
      ],
    },
    {
      heading: 'Step by step: from a Figma screen to a 3D App Store screenshot',
      body: [
        'For the 3D workflow you export the app screen itself (not the finished screenshot) from Figma, at the device’s native resolution, so it maps onto the 3D display without stretching. The iPhone 18 Pro Max display, for example, is 1320 × 2868 px and the iPhone 18 Pro display is 1206 × 2622 px.',
      ],
      bullets: [
        'Design the app screen in Figma at the target device’s screen proportions and export it as PNG, JPEG or WebP.',
        'Open the [free Ecranify editor](/free) (no account needed), pick a device such as the iPhone 18 Pro Max or Pixel 10 Pro XL, and drop the exported screen onto its display.',
        'Drag to orbit the camera to the angle you want and set a gradient or image background. The full editor (after signing in) adds lighting, device rotation, and headlines as 2D or 3D text.',
        'Choose a store export preset (iPhone 6.9″ 1320 × 2868, iPad 13″ 2064 × 2752, Google Play phone 1080 × 1920 and others) and export.',
      ],
    },
    {
      heading: 'Continuous carousel strips without manual slicing',
      body: [
        'A continuous layout — a background, headline or device that runs across two or more screenshots — is fragile in Figma: the frames have to be sliced at exact offsets, and reordering the set breaks the seams.',
        'In the Ecranify editor you compose the strip as one scene and the carousel export slices it into 2–5 frames, each at the exact store size. A configurable gap (5% of the frame width by default, up to 15%) leaves an empty gutter between slices to match the spacing the store adds between gallery images, so a device that straddles a seam still lines up. Carousel export is part of the paid editor.',
      ],
    },
    {
      heading: 'ASO tips for designer-led screenshots',
      body: [
        'A clean workflow only matters if the result converts. The broader strategy — ordering, captions and testing — is in our [App Store screenshot best practices](/guides/app-store-screenshot-best-practices); these are the points that trip up Figma-first teams most often:',
      ],
      bullets: [
        'Lead with the benefit: put a short headline in the top third of the frame, where the device doesn’t cover it.',
        'Enlarge the UI: real in-app text is often too small to read in search results, so increase font sizes and contrast on screens made for the store.',
        'Keep one camera setup: use the same device angle and lighting across a set so it reads as one coherent story.',
        'Respect the edges: keep key UI away from the frame edges and the device corners so nothing important is clipped.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/iphone-6-9.jpg',
      alt: '3D iPhone mockup rendered from a Figma screen export',
      caption: 'A screen exported from Figma, mapped onto a 3D iPhone in Ecranify.',
    },
    {
      src: '/marketing/examples/carousel.jpg',
      alt: 'A continuous 3-frame carousel screenshot set built from Figma screens',
      caption: 'One continuous background and device strip, sliced into store-size frames.',
      wide: true,
    },
  ],
  faq: [
    {
      q: 'What frame size should I use in Figma for App Store screenshots?',
      a: 'Use the exact store size and export at 1x: 1320 × 2868 px for the 6.9-inch iPhone slot and 2064 × 2752 px for the 13-inch iPad slot. For Google Play phones, 1080 × 1920 px is a safe choice within the accepted 320–3840 px range.',
    },
    {
      q: 'Why is my Figma PNG rejected by App Store Connect or Google Play?',
      a: 'Figma always exports PNGs with an alpha channel, even with a solid background. Google Play requires images without alpha and App Store Connect can reject them too. Export JPG instead, or remove the alpha channel from the PNG before uploading.',
    },
    {
      q: 'Should I export at 2x or 3x for sharper screenshots?',
      a: 'Only if the multiplied size lands exactly on an accepted store size. The simplest approach is to build frames at the final pixel size and export at 1x, so nothing is resampled.',
    },
    {
      q: 'Can I upload SVG files from Figma into Ecranify?',
      a: 'No. Screen images and custom decals accept PNG, JPEG or WebP. Export vector artwork from Figma as a high-resolution PNG first.',
    },
    {
      q: 'Can I match the device color to my Figma design?',
      a: 'Yes. In the Ecranify editor you can change the device body color and its roughness and metalness, and set the background to match your brand palette.',
    },
  ],
  cta: {
    title: 'Turn your Figma screens into 3D App Store screenshots',
    body: 'Drop a Figma export onto a 3D device, pick the angle and lighting, and export at the exact App Store or Google Play size. Free to start — no account needed.',
  },
}

// ── How to Make App Store Screenshots ───────────────────────────────

const howToMake: GuideData = {
  slug: 'how-to-make-app-store-screenshots',
  canonicalPath: '/guides/how-to-make-app-store-screenshots',
  title: 'How to Make App Store Screenshots That Convert (2026 Guide) | Ecranify',
  description:
    'Learn how to make app store screenshots that drive downloads. This step-by-step tutorial covers capturing screens, choosing 3D mockups, adding headlines, and exporting the exact sizes.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'How to make App Store screenshots (step by step)',
  intro: [
    'Your app store screenshots are the most important marketing assets you have. Studies show that over 70% of users decide whether to download an app based entirely on its screenshots, without ever reading the description. Creating app store screenshots that convert requires a mix of clean UI, clear benefits, and professional design.',
    'This step-by-step app store screenshot tutorial will walk you through exactly how to create compelling, high-converting screenshots for iOS and Google Play—from capturing your best screens to exporting all required sizes in minutes.',
  ],
  updated: 'October 2026',
  readingTime: '8 min read',
  sections: [
    {
      heading: 'Why App Store screenshots matter for conversion',
      body: [
        'The App Store and Google Play are highly visual platforms. When users search for an app, the first three screenshots dominate the screen. Over 70% of users make their download decision based entirely on these images. If your screenshots look unprofessional or fail to explain what the app does, users will bounce to a competitor.',
        'Good screenshots do more than just show the UI—they tell a story, highlight key benefits, and build trust before the app is even opened.',
      ],
    },
    {
      heading: 'Understand Apple and Google requirements',
      body: [
        'Before designing, it is crucial to understand the rules. Both stores have strict requirements for dimensions, formats, and aspect ratios. For example, Apple requires the 6.9″ iPhone size, while Google Play is more flexible but strict on aspect ratio. For a complete list of required dimensions, check out our [App Store screenshot sizes guide](/guides/app-store-screenshot-sizes).',
        'Generally, design for the largest required size (like 1320 × 2868 for iOS) and let the stores or your export tool scale them down for older devices.',
      ],
    },
    {
      heading: 'Step 1: Capture your best screens',
      body: [
        'Start by taking clean, high-resolution screenshots of your app’s most important features. Avoid cluttered screens or deep settings menus. Instead, focus on the "aha moments"—the screens where your app delivers the most value.',
        'Make sure to hide the status bar (time, battery, signal) or use a clean status bar tool, as messy notifications look unprofessional. Consider whether light or dark mode suits your brand better, and stick to one theme consistently across the set.',
      ],
    },
    {
      heading: 'Step 2: Choose a device frame',
      body: [
        'Raw, flat screenshots rarely convert as well as framed ones. Placing your UI inside a device frame provides context and makes the app look tangible. 3D device mockups are particularly effective because they add depth, premium lighting, and dynamic angles that flat vector frames cannot match. If you currently use a flat-frame template tool, our [AppMockUp alternative comparison](/alternatives/appmockup) shows what changes when you move to 3D.',
        'Tools like Ecranify allow you to drop your UI into high-quality 3D iPhone and Android models, instantly elevating the perceived value of your app without requiring complex 3D software. If you are choosing between 3D mockup tools, our [Ecranify vs Rotato comparison](/vs/rotato) covers the trade-offs.',
      ],
    },
    {
      heading: 'Step 3: Add headlines and backgrounds',
      body: [
        'Do not rely on your UI to explain itself. Add a short, bold headline above or below the device in each frame. The golden rule of copywriting here is: focus on benefits, not features. Instead of "Advanced Task Filtering," write "Find what matters, faster."',
        'Use contrasting background colors to make the device and text pop. A subtle gradient or a solid brand color works best. Ensure the text is large enough to read easily on a small mobile screen.',
      ],
    },
    {
      heading: 'Step 4: Design a carousel flow',
      body: [
        'Modern store listings often use a connected carousel design, where a single background or a 3D device spans across multiple screenshot frames (see how to size and slice [panoramic App Store screenshots](/guides/panoramic-app-store-screenshots)). This encourages users to swipe through the gallery, increasing engagement and the chance of a download.',
        'By telling a continuous story across the frames, you keep the user interested. If you use Figma for UI design, you can read our [Figma to App Store screenshots workflow](/guides/figma-app-store-screenshots) on how to integrate your designs into a seamless carousel strip.',
      ],
    },
    {
      heading: 'Step 5: Export in all required sizes',
      body: [
        'Manually resizing your design for the 6.9″ iPhone, 6.5″ iPhone, iPad, and various Android tablet sizes is tedious. The final step is exporting your composition into the exact dimensions required by the App Store and by [Google Play’s screenshot requirements](/guides/google-play-screenshot-requirements).',
        'Ecranify simplifies this with built-in multi-size export presets. Design your layout once, select the required devices, and export a complete, store-ready package in a single click.',
      ],
    },
    {
      heading: 'Common mistakes to avoid',
      body: ['When you create app store screenshots, watch out for these conversion-killers:'],
      bullets: [
        'Using raw UI screenshots with no text or device frame context.',
        'Writing long, small paragraphs of text that are impossible to read on mobile.',
        'Forgetting to update the screenshots when the app’s UI drastically changes.',
        'Cluttered status bars showing low battery or irrelevant notifications.',
        'Placing important text at the very edges where it might get cropped by smaller screens.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/iphone-6-9.jpg',
      alt: '3D iPhone mockup with bold headline',
      caption: "Add bold, benefit-driven headlines to explain your app's value.",
    },
    {
      src: '/marketing/examples/carousel.jpg',
      alt: 'App store screenshot carousel design spanning multiple frames',
      caption: 'A connected carousel encourages users to swipe through the gallery.',
      wide: true,
    },
  ],
  faq: [
    {
      q: 'How do I make screenshots for the App Store?',
      a: 'To make App Store screenshots, capture your app’s core features, place them in high-quality device frames (like 3D mockups), add benefit-driven headlines, and export them at the exact required sizes (e.g., 1320 × 2868 for iPhone 6.9″).',
    },
    {
      q: 'Do I need 3D mockups for my screenshots?',
      a: 'While not strictly required, 3D mockups add depth, professionalism, and realism to your listing, often resulting in higher conversion rates compared to flat, raw UI images.',
    },
    {
      q: 'What is the best tool to create app store screenshots?',
      a: 'Ecranify is a great choice because it combines realistic 3D device mockups, custom backgrounds, text tools, and built-in export presets for all Apple and Google required sizes directly in your browser.',
    },
    {
      q: 'How many screenshots should I upload?',
      a: 'Apple allows up to 10 screenshots and Google Play allows up to 8. You should upload at least 3 to 5 to fully showcase your app’s main benefits, with the strongest feature in the first frame.',
    },
    {
      q: 'Can I use the same screenshots for iOS and Android?',
      a: 'You can share the core design and text, but you should use iOS devices for the App Store and Android devices for Google Play to meet user expectations. You must also export them at different sizes.',
    },
  ],
  cta: {
    title: 'Create App Store screenshots that convert',
    body: 'Use Ecranify’s 3D device mockups, carousel maker, and one-click store exports to design your perfect App Store and Google Play listing.',
  },
}

// ── App Store screenshot best practices ─────────────────────────────

const bestPractices: GuideData = {
  slug: 'app-store-screenshot-best-practices',
  canonicalPath: '/guides/app-store-screenshot-best-practices',
  title: 'App Store Screenshot Best Practices for 2026 | Ecranify',
  description:
    'App Store screenshot best practices for 2026, checked against Apple’s guidance: what shows in search, the first frame, captions, Dark Mode, iPad and A/B tests.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'App Store screenshot best practices for 2026',
  intro: [
    'The App Store screenshot best practices that matter most are simple: put your core benefit in the first one to three screenshots, because those are the ones Apple shows in search results when you have no app preview, and give every following frame one clear benefit. Then write captions people can read at thumbnail size, add Dark Mode and iPad screenshots if your app supports them, and test changes with product page optimization instead of guessing.',
    'Each practice below is tied to what Apple and Google actually document, not to unsourced conversion statistics. If you want the step-by-step production process instead, start with our [step-by-step guide to making App Store screenshots](/guides/how-to-make-app-store-screenshots).',
  ],
  updated: 'October 2026',
  readingTime: '8 min read',
  sections: [
    {
      heading: 'Lead with your strongest screenshot',
      body: [
        'Apple’s product page guidance says that, depending on orientation, the first one to three images appear in search results when no app preview is available, and asks you to make sure they “highlight the essence of your app.” Most people decide from search results, so your clearest benefit belongs in frame one, not on frame five. From iOS 27 and iPadOS 27, a dedicated [Search Results creative asset](/guides/app-store-header-search-results-assets) can take that spot; if you don’t upload one, your screenshots and app previews still appear.',
        'A common mistake is opening with a splash screen or logo. People already see your app name and icon next to the screenshots, so the first frame should show what the app does. A set that shows only title art, a login page or a splash screen can even [get the listing rejected under guideline 2.3.3](/guides/app-store-screenshot-rejection-reasons).',
      ],
    },
    {
      heading: 'What App Store search shows, and what it ranks on',
      body: [
        'Apple’s App Store search page says your rating and up to three screenshots or app previews may display in search results, depending on the platform and image orientation, so check how your set looks in both portrait and landscape layouts. Apple’s one-to-three rule applies when no app preview is available; if you upload one, previews autoplay muted and show a poster frame when they don’t play, so the opening seconds and poster frame carry the job of frame one.',
        'Screenshots do not add keywords. Apple lists the search ranking factors as text relevance (your app name, subtitle, keyword field and primary category) and user behavior such as downloads, ratings and reviews. Caption text on a screenshot is not on that list, so write captions for people, not for the algorithm. Screenshots still matter for ASO: they drive the tap-through and conversion that feed the user-behavior side.',
      ],
    },
    {
      heading: 'Write benefit-led captions, not feature lists',
      body: [
        'Every screenshot should carry a short, bold caption, and the wording matters. Features describe what the app does (“Advanced filtering options”); benefits describe what the user gets (“Find exactly what you need, instantly”). Apple’s guidance asks you to focus each screenshot after the first on a main benefit or feature, so one frame, one message.',
        'Keep captions to one or two short lines in large, high-contrast type. If the caption is hard to read when the screenshot is the size of a postage stamp, it is too small or too long.',
      ],
    },
    {
      heading: 'Design for thumbnail size first',
      body: [
        'In search results each screenshot takes up only a fraction of the phone screen. At that size the fine details of your UI disappear; what remains are colors, shapes, the device and the caption.',
        'Design with that constraint: bold backgrounds, strong contrast between device and background, and captions that stay readable when scaled down. A quick test is to zoom your design out to about 20% — if you can still read the caption and understand the frame, it works.',
      ],
    },
    {
      heading: 'Tell a story across the carousel',
      body: [
        'Treat the set as a sequence, not a random collection of screens. A common pattern: frame 1 = the core benefit, frames 2–4 = supporting features, a later frame = trust signals such as awards or press, as long as they are real and current.',
        "Connected carousel designs, where one background or device spans several frames, create visual continuity that invites a swipe. Getting the slice sizes and seams right is its own craft — see [panoramic App Store screenshots](/guides/panoramic-app-store-screenshots). Ecranify's carousel export renders one wide composition and slices it into individual store-ready frames.",
      ],
    },
    {
      heading: 'Localize for your top markets',
      body: [
        'Apple asks developers to localize the description, keywords, app previews and screenshots for each market they offer the app in. At minimum, translate the caption text in each frame; for bigger impact, also capture the UI in that language so people see the app as they will use it. Some screenshot tools build localization in; our [Screenshots Pro comparison](/vs/screenshots-pro) looks at one of them.',
        'The App Store allows up to 10 screenshots per localization and device size; Google Play allows up to 8 per device type. Prioritize languages by revenue or downloads rather than translating everything at once.',
      ],
    },
    {
      heading: 'Include Dark Mode if your app supports it',
      body: [
        'Apple’s product page guidance is direct: if your app supports Dark Mode, consider including at least one screenshot that shows what that experience looks like. There is no universal winner for the overall background; dark backgrounds make light UIs pop, light backgrounds keep dark UIs from feeling heavy.',
        'Whatever you pick, keep the set consistent: the same fonts, caption position, device finish and background family across every frame, with the Dark Mode frame clearly part of the same story.',
      ],
    },
    {
      heading: "Don't skip iPad screenshots",
      body: [
        'If your app runs on iPad, App Store Connect requires at least one screenshot for the iPad 13″ display (2064 × 2752 or 2048 × 2732 px in portrait). Stretching phone screenshots or reusing the phone layout wastes the slot.',
        'Show how the app uses the larger screen: split views, sidebars and denser layouts are worth highlighting. Our App Store screenshot sizes guide lists every iPhone and iPad dimension.',
      ],
    },
    {
      heading: 'A/B test with product page optimization',
      body: [
        "Apple's product page optimization lets you test up to three treatments with alternate icons, screenshots and app previews against your original page. You choose what share of traffic goes to the test, one test runs at a time for up to 90 days, and treatments can be localized. Alternate icons must already be in the app binary, and tests are not available for custom product pages.",
        'Apple recommends waiting until a treatment is declared better or worse than the baseline with at least 90% confidence before applying it. Test one variable at a time: a different first screenshot, caption style or background. On Google Play, store listing experiments compare up to two variants with your current listing, with one default graphics experiment or up to five localized experiments at once. For a before-and-after example outside a formal test, see our [case study of a screenshot redesign that lifted downloads 80%](/guides/screenshot-case-study-80-percent-more-downloads).',
      ],
    },
    {
      heading: 'Common mistakes that kill conversion',
      body: ['These patterns consistently underperform — avoid them:'],
      bullets: [
        'Opening with a logo, splash screen, or “welcome” frame instead of showing the app in action.',
        'Captions that are unreadable at thumbnail size in search results.',
        'Showing raw UI with no device frame, caption, or background context.',
        'Inconsistent visual style across frames — mixing fonts, colors, or device orientations.',
        "Uploading identical screenshots for phone and tablet instead of showcasing each device's strengths.",
        'Never updating screenshots after a major redesign — stale screenshots erode trust.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/iphone-6-9.jpg',
      alt: 'An App Store screenshot with a bold, benefit-led headline on a 3D iPhone mockup',
      caption: 'Lead with a bold headline that communicates a clear benefit.',
    },
    {
      src: '/marketing/examples/carousel.jpg',
      alt: 'A connected carousel screenshot strip spanning three App Store frames',
      caption: 'A connected carousel tells a story and encourages swiping.',
      wide: true,
    },
  ],
  faq: [
    {
      q: 'How many App Store screenshots should I upload?',
      a: 'Apple allows up to 10 per device size and localization, and Google Play up to 8 per device type. Upload enough to cover your main benefits, one per frame, with the strongest in the first frame because the first one to three can appear in search results.',
    },
    {
      q: 'How many screenshots show in App Store search results?',
      a: 'Up to three screenshots or app previews, depending on the platform and image orientation. When no app preview is available, Apple says the first one to three screenshots appear, so put your strongest benefit there.',
    },
    {
      q: 'Do screenshot captions help App Store keyword ranking?',
      a: 'No. Apple lists text relevance (app name, subtitle, keywords, primary category) and user behavior such as downloads, ratings and reviews as the search ranking factors. Captions help by improving conversion, not by adding keywords.',
    },
    {
      q: 'Should I use dark or light backgrounds?',
      a: "It depends on your app's UI and brand. Dark backgrounds make light UIs pop; light backgrounds keep dark UIs from feeling heavy. If your app supports Dark Mode, Apple suggests including at least one screenshot that shows it.",
    },
    {
      q: 'What is product page optimization?',
      a: "Apple's tool for A/B testing up to three treatments with alternate icons, screenshots and app previews against your original product page, for up to 90 days. Google Play's store listing experiments test up to two variants against the current listing.",
    },
    {
      q: 'Do I need separate screenshots for iPhone and iPad?',
      a: 'If your app runs on iPad, yes: App Store Connect requires iPad 13-inch screenshots (2064 × 2752 or 2048 × 2732 px in portrait). Show how the app uses the larger screen.',
    },
  ],
  cta: {
    title: 'Design screenshots that convert',
    body: 'Apply these best practices in Ecranify — 3D device mockups, headline text, connected carousels, and one-click multi-size export. Free to start.',
  },
}

// ── iPhone 18 Pro & the iPhone Duo (foldable): screenshots ─────────

const foldableScreenshots: GuideData = {
  slug: 'iphone-18-iphone-ultra-app-store-screenshots',
  canonicalPath: '/guides/iphone-18-iphone-ultra-app-store-screenshots',
  title: "iPhone 18 Pro & iPhone Duo (Foldable): App Store Screenshots After Apple's September 2026 Event | Ecranify",
  description:
    'Apple announced the iPhone 18 Pro, iPhone 18 Pro Max and its first foldable — the iPhone Duo — on September 9, 2026. What is confirmed, what the earlier "iPhone Ultra" rumours got wrong, and what a folding iPhone changes about your App Store screenshots.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: "iPhone 18 Pro and the iPhone Duo: what Apple's first foldable means for your App Store screenshots",
  intro: [
    'On September 9, 2026, Apple announced the iPhone 18 Pro, the iPhone 18 Pro Max and its first folding iPhone. The foldable is officially called the iPhone Duo — not the "iPhone Ultra" or "iPhone Fold" that most reporting had settled on. For app developers the interesting part is not the titanium hinge. It is that a device which changes shape in your hand breaks the single assumption every App Store screenshot has been built on for fifteen years: that a screen has one size.',
    'This guide has been updated after the event. It leads with what Apple confirmed, flags which pre-announcement rumours were wrong, and then works through the question Apple did not answer on stage — what App Store screenshots for a foldable iPhone will actually have to look like.',
  ],
  updated: 'October 2026',
  readingTime: '11 min read',
  sections: [
    {
      heading: 'Update — September 9, 2026: Apple announced the iPhone Duo',
      body: [
        'Apple\'s ["Surprise and shine" event](/guides/apple-september-2026-lineup-app-store-screenshots) confirmed three phones: the iPhone 18 Pro (6.3″), the iPhone 18 Pro Max (6.9″), and the iPhone Duo, a book-style foldable with a roughly 7.6″ inner display that opens out of a roughly 5.4″ cover display. The iPhone 18 Pro models went on sale September 18, 2026; the iPhone Duo follows on October 23, 2026, with pre-orders from October 16. There is no standard non-Pro iPhone 18 this year — those models are expected in spring 2027.',
        'The short version for your store listing: the iPhone 18 Pro family changes nothing you have to act on, and the iPhone Duo is a real new form factor but Apple did not ship a foldable App Store screenshot slot alongside it. The rest of this guide is unchanged in its reasoning — only now the hardware is fact rather than rumour.',
      ],
      table: {
        caption: 'Foldable iPhone: rumoured vs. what Apple confirmed on September 9, 2026',
        headers: ['Point', 'Pre-event rumour', 'Confirmed at the event'],
        rows: [
          ['Name', '"iPhone Ultra" / "iPhone Fold"', 'iPhone Duo'],
          ['Inner display', '~7.8″, 2,713 × 1,920', '~7.6″, book-style, roughly 4:3'],
          ['Cover display', '~5.5″, 2,088 × 1,422', '~5.4″'],
          ['On sale', 'September–October 2026, US first', 'October 23, 2026 (pre-orders October 16)'],
          ['Biometrics', 'Touch ID power button, no Face ID', 'Confirmed — Touch ID in the side button'],
          ['Thickness', '~4.5 mm open', "Confirmed — 4.5 mm open, Apple's thinnest iPhone"],
          ['SIM', 'Not discussed', 'eSIM-only worldwide — no physical SIM tray in any market'],
          [
            'Front camera',
            'Hole-punch on both screens',
            'Under-display camera on the inner screen, plus a corner selfie camera',
          ],
          ['Starting price', 'Roughly $2,000–2,400', '$1,999 for 256 GB (up to ~$3,199 for 2 TB)'],
        ],
      },
    },
    {
      heading: 'When the iPhone 18 Pro and iPhone Duo go on sale',
      body: [
        'Apple confirmed the dates on stage. The iPhone 18 Pro and iPhone 18 Pro Max opened for pre-order on Friday, September 12 and went on sale Friday, September 18, 2026 in more than 65 countries. The iPhone Duo is on a separate, later track: pre-orders from Friday, October 16, retail availability Friday, October 23, 2026.',
        'The split matters for planning because it is two screenshot-refresh moments in one product cycle, not one. And with the non-Pro iPhone 18 held back to spring 2027, there is a third moment still to come.',
      ],
      table: {
        caption: 'Confirmed 2026 Apple iPhone timeline',
        headers: ['Milestone', 'Date', 'Status'],
        rows: [
          ['"Surprise and shine" keynote', 'September 9, 2026', 'Happened'],
          ['iPhone 18 Pro / Pro Max pre-orders', 'September 12, 2026', 'Confirmed'],
          ['iPhone 18 Pro / Pro Max on sale', 'September 18, 2026', 'Confirmed'],
          ['iPhone Duo pre-orders', 'October 16, 2026', 'Confirmed'],
          ['iPhone Duo on sale', 'October 23, 2026', 'Confirmed'],
          ['iPhone 18 (standard, non-Pro)', 'Spring 2027', 'Expected'],
        ],
      },
    },
    {
      heading: 'iPhone 18 Pro: still a marketing refresh, not a compliance one',
      body: [
        "For screenshot purposes the iPhone 18 Pro is a non-event, and the event confirmed it. The Pro keeps the 6.3″ display and the Pro Max the 6.9″ display — the same sizes as the iPhone 17 Pro generation. Face ID and the Dynamic Island both stay; the Island is smaller and now shows up to three simultaneous Live Activities. The headline changes are internal: the A20 Pro chip on a 2nm process, Apple's C2 modem, a variable-aperture main camera, storage up to 2 TB for the first time, and a new Burgundy colour. Prices rose $100 across the line.",
        "Practically: if your listing already uses the 6.9″ App Store slot at 1320 × 2868 px, the iPhone 18 Pro Max does not force you to re-export anything. What changes is the mockup — a listing that shows last year's handset, or a wider Dynamic Island, reads as slightly stale to a shopper holding this year's. That is a marketing refresh, not a compliance one.",
      ],
      bullets: [
        'Display sizes unchanged: 6.3″ Pro, 6.9″ Pro Max — existing 1320 × 2868 exports stay valid.',
        'Smaller Dynamic Island (now three Live Activities) — a detail in the device frame, not a canvas-size change.',
        'New Burgundy colour and a slightly thicker camera plateau are the visible design deltas worth reflecting in a mockup.',
        'Prices up $100 and a 2 TB tier — the polish bar on a listing goes up with the price.',
      ],
    },
    {
      heading: 'iPhone Duo: the foldable, by the numbers',
      body: [
        "The folding iPhone is the one that matters here, and Apple filled in the specifics. It is a book-style fold: a roughly 7.6″ inner display — closer to an iPad's 4:3 than to any iPhone aspect ratio — opening out of a roughly 5.4″ cover display, with a crease Apple describes as nearly invisible when unfolded. At 4.5 mm open it is the thinnest iPhone Apple has made, in a titanium-and-aluminium frame on a liquid-metal hinge.",
        'Two confirmed details reshape every mockup of this device. Biometrics: there is no Face ID — the iPhone Duo uses Touch ID built into the side button, like an iPad. And it is eSIM-only in every market, so there is no SIM tray anywhere on the body. The inner selfie camera sits under the display; a second camera is in the corner. Every screenshot you draw of this device needs a different front-face and edge story than any current iPhone.',
      ],
      table: {
        caption: 'Confirmed iPhone Duo specifications',
        headers: ['Spec', 'Confirmed value'],
        rows: [
          ['Inner display', '~7.6″, book-style, roughly 4:3'],
          ['Cover display', '~5.4″'],
          ['Thickness', "4.5 mm open (Apple's thinnest iPhone), ~9–9.5 mm closed"],
          ['Frame / hinge', 'Titanium and aluminium, liquid-metal hinge, "nearly invisible" crease'],
          ['Biometrics', 'Touch ID in the side button (no Face ID)'],
          ['SIM', 'eSIM-only worldwide — no physical SIM tray'],
          [
            'Cameras',
            '48 MP Dual Fusion rear (main + ultra-wide, no telephoto); under-display inner camera + corner selfie camera',
          ],
          ['Chip / RAM', 'A20 Pro (2nm), 12 GB'],
          ['Accessory', 'Apple Pencil (USB-C) support on both displays'],
          ['Price', '$1,999 for 256 GB, up to ~$3,199 for 2 TB'],
          ['On sale', 'October 23, 2026 (pre-orders October 16)'],
        ],
      },
    },
    {
      heading: 'Why a folding iPhone breaks the screenshot model',
      body: [
        'Every App Store screenshot slot today is a fixed pixel rectangle tied to a device class. You design one 1320 × 2868 image, Apple scales it down for older iPhones, done. A foldable has two screens with two different aspect ratios and a live transition between them — and Apple\'s own iPhone Duo developer material tells developers to stop designing around fixed screen assumptions and let interfaces resize fluidly. Apple published tech-talk sessions ("Design for iPhone Duo", "adaptive layouts on iPhone Duo", "multiple displays and scenes on iPhone Duo") the same week, with a dedicated Human Interface Guidelines document to follow.',
        "That guidance is about your app's UI. But your store listing is the first screen a buyer sees, and it has the same problem one level up: a screenshot that shows a tall narrow phone UI tells an iPhone Duo owner nothing about what your app does with 7.6 inches. Conversely, a screenshot that only shows the expansive unfolded layout misrepresents the app for the majority of buyers on a normal iPhone.",
      ],
    },
    {
      heading: 'What App Store screenshots for the iPhone Duo could look like',
      body: [
        'Apple did not announce a new App Store Connect display class for the iPhone Duo at the event — it pointed developers at adaptive-layout sessions and a forthcoming iPhone Duo Human Interface Guidelines document instead. So this is still open, and what follows is reasoning from precedent rather than fact. There are three plausible outcomes, and they are worth designing against now because the work is nearly identical in each case.',
        "The most likely, based on how Apple handled the 6.9″ transition, is that the iPhone Duo initially inherits scaled 6.9″ screenshots and a dedicated slot appears only once the device has meaningful install base. The second possibility is a new display class in App Store Connect at the inner panel's native resolution. The third — and the one that would actually serve shoppers best — is a slot that expects the unfolded canvas, with a separate optional cover-screen size.",
      ],
      table: {
        caption: 'Three plausible foldable screenshot scenarios (speculative)',
        headers: ['Scenario', 'What you would upload', 'What it means for you'],
        rows: [
          [
            'Scaled from 6.9″',
            'Nothing new — 1320 × 2868 as today',
            'Zero compliance work; your listing simply looks narrow on a 4:3 screen',
          ],
          [
            'New inner-display class',
            "A portrait image at the inner panel's ~4:3 native resolution",
            'A second full design pass at an iPad-like aspect ratio',
          ],
          [
            'Inner + optional cover slot',
            'One unfolded ~4:3 image plus one narrower cover-screen image',
            'Two crops per frame; the story has to work at both widths',
          ],
        ],
      },
    },
    {
      heading: 'How to design a screenshot that survives a fold',
      body: [
        'Whichever slot Apple ships, the design constraints are already predictable, and they are the same constraints Android developers learned on the Galaxy Z Fold. Composition has to tolerate a change of aspect ratio without the message falling apart.',
        'The single most useful habit is to design the message and the device frame as separable layers. A headline anchored to the top of the canvas and a device mockup anchored to the bottom will re-flow into a wider frame; a full-bleed illustration where text is baked into the artwork will not.',
      ],
      bullets: [
        'Keep headlines in the top third and short enough to survive a 30–40% width change without rewrapping.',
        'Show the fold. A frame where the device is open, with content spanning both halves, communicates "this app uses your big screen" faster than any caption.',
        'Use at least one frame showing the cover screen, so buyers understand the app works closed too.',
        'Avoid putting anything critical along the vertical centre line of an unfolded mockup — the crease still reads visually even though Apple calls it nearly invisible.',
        'Design the carousel story so it still makes sense if a frame is cropped: the first frame must carry the value proposition alone.',
        'Do not bake device chrome into your captured screen image. Capture the raw UI and add the frame separately, so the same capture can be re-framed for a different body.',
      ],
    },
    {
      heading: 'What Apple confirmed, and what is still open',
      body: [
        'After the event the hardware is settled: the iPhone Duo name, the ~7.6″ inner and ~5.4″ cover displays, the 4.5 mm open thickness, Touch ID in the side button, eSIM-only, the under-display inner camera, the $1,999 starting price and the October 23 on-sale date are all confirmed by Apple. The earlier "iPhone Ultra" name, the ~7.8″ / 5.5″ display figures and the ~$2,400 starting price were the rumours that missed.',
        'What is still open is the store side. Apple has not published an App Store Connect screenshot size or display class for the iPhone Duo. The safe planning position is unchanged: treat the iPhone 18 Pro refresh as near-zero-effort, treat a foldable screenshot slot as a maybe that costs you one extra design pass, and build both from source files you can re-export rather than from flattened images you would have to redraw.',
      ],
    },
    {
      heading: 'Where Ecranify already fits',
      body: [
        "For the iPhone 18 Pro family, nothing about your workflow changes. Because the Pro and Pro Max keep the 6.3″ and 6.9″ display sizes, the App Store export presets you use today — 1320 × 2868 for the mandatory 6.9″ slot — stay valid, and Ecranify's existing iPhone Pro Max mockups work out of the box. Swap in a fresh screen capture, re-export, done; adjust the device colour to the new Burgundy if you want the mockup to read as current.",
        'The iPhone Duo is the more interesting case, and it is exactly the case a parametric tool is built for. Devices in Ecranify are not fixed 3D models — [every mockup is generated from parameters](/guides/how-we-build-3d-device-mockups): body width, height and depth, corner radius, edge bevel, front-face inset, plus positioned features like the camera lens, display cutout, Dynamic Island, buttons, speaker grills and custom decals. Building a new device means changing numbers, not modelling a mesh.',
        "So recreating the iPhone Duo's unfolded body is an afternoon of slider work rather than a wait for someone else to publish a template: set the ~4:3 unfolded body dimensions, dial in the 4.5 mm profile, place the under-display camera, drop the SIM tray entirely (it is eSIM-only), move Face ID off and Touch ID onto the side button, and export at whatever size App Store Connect ends up asking for. Whatever Apple's first folding iPhone needs from your listing, the tool does not need a hardware update before you can mock it up.",
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/iphone-6-9.jpg',
      alt: 'App Store screenshot at the iPhone 6.9-inch size with a 3D iPhone Pro Max mockup',
      caption: 'The 6.9″ slot the iPhone 18 Pro Max keeps using — already a preset in Ecranify.',
    },
    {
      src: '/marketing/examples/ipad-13.jpg',
      alt: 'App Store screenshot at the iPad 13-inch size showing a wide tablet layout',
      caption: 'A 4:3 tablet-shaped canvas is the closest thing today to an unfolded foldable frame.',
    },
    {
      src: '/marketing/examples/carousel.jpg',
      alt: 'Connected carousel screenshot strip spanning three App Store frames',
      caption: 'Carousel storytelling still works when the canvas gets wider — if the layers stay separable.',
      wide: true,
    },
  ],
  faq: [
    {
      q: 'When do the iPhone 18 Pro and iPhone Duo come out?',
      a: 'Apple confirmed both at its September 9, 2026 event. The iPhone 18 Pro and iPhone 18 Pro Max went on sale September 18, 2026. The iPhone Duo goes on sale October 23, 2026, with pre-orders from October 16. The standard non-Pro iPhone 18 is expected separately in spring 2027.',
    },
    {
      q: 'Is the foldable iPhone called iPhone Ultra?',
      a: 'No. Most pre-announcement reporting used "iPhone Ultra" (and sometimes "iPhone Fold"), but Apple named it the iPhone Duo at its September 2026 event.',
    },
    {
      q: 'Will I need new App Store screenshots for the iPhone 18 Pro?',
      a: 'No. The iPhone 18 Pro and Pro Max keep the 6.3″ and 6.9″ display sizes, so the mandatory 1320 × 2868 px 6.9″ screenshots you already have remain valid. Refreshing the device mockup — including the smaller Dynamic Island and the new Burgundy colour — is a marketing choice, not a requirement.',
    },
    {
      q: 'What screenshot size does the iPhone Duo need?',
      a: "Apple has not published one. It did not add an App Store Connect display class for the iPhone Duo at the launch event, pointing developers to adaptive-layout guidance instead. The likeliest short-term outcome is that Apple scales existing 6.9″ screenshots until the device has real install base; a dedicated slot would plausibly be a portrait image at the inner panel's ~4:3 native resolution.",
    },
    {
      q: 'How should I design screenshots that work folded and unfolded?',
      a: 'Keep headline and device mockup as separate layers so the composition re-flows, keep headlines short enough to survive a width change, show at least one open-device frame and one cover-screen frame, and keep critical content off the vertical centre line.',
    },
    {
      q: 'Can I mock up the iPhone Duo before it ships?',
      a: 'Yes. Ecranify generates every device from parameters — body dimensions, corner radius, bevel, front-face inset, camera and cutout placement, buttons and decals — so a new form factor is a matter of adjusting values rather than waiting for a template.',
    },
  ],
  cta: {
    title: 'Ready for the iPhone 18 Pro — and the iPhone Duo',
    body: "Ecranify ships current iPhone Pro Max mockups with App Store export presets built in, and its parametric device editor lets you build the iPhone Duo's unfolded body yourself. Free to start.",
  },
}

// ── Apple's September 2026 lineup: what changes for your screenshots ─

const appleSeptember2026: GuideData = {
  slug: 'apple-september-2026-lineup-app-store-screenshots',
  canonicalPath: '/guides/apple-september-2026-lineup-app-store-screenshots',
  title: 'Apple September 2026 Lineup: Screenshot Changes | Ecranify',
  description:
    "Which of Apple's September 2026 devices change your App Store screenshots: the iPhone 18 Pro needs nothing, the iPhone Duo now has its own required sizes.",
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: "Apple's September 2026 lineup and your App Store screenshots",
  intro: [
    'Apple\'s "Surprise and shine" event on September 9, 2026 was one of its busiest hardware days in years: the iPhone 18 Pro and iPhone 18 Pro Max, the iPhone Duo (Apple\'s first foldable), Apple Watch Series 12 and Apple Watch Ultra 4, and AirPods 5. Only one of them changes your App Store screenshots: the iPhone Duo, which now has two screenshot sizes of its own — 1398 × 2034 px for the outer display and 2007 × 2853 px for the inner one, optional today and required from April 2027 for any app submitted with the iOS 27.1 SDK or later.',
    'Nothing else in the lineup needs a re-upload. This guide walks through everything Apple announced, separates the changes that touch your App Store or Google Play listing from the ones that do not, and shows what each means in practice inside Ecranify.',
  ],
  updated: 'October 2026',
  readingTime: '8 min read',
  sections: [
    {
      heading: 'Everything Apple announced on September 9, 2026',
      body: [
        'Five product lines, two on-sale tracks. The iPhone 18 Pro models and the wearables shipped in September; the iPhone Duo follows in late October. There is no standard non-Pro iPhone 18 this cycle — Apple is expected to launch those in spring 2027.',
        'Only one of these — the iPhone Duo — introduces a genuinely new screen shape, and it is the only one that has since brought new App Store screenshot sizes with it. Everything else is a refresh.',
      ],
      table: {
        caption: "Apple's September 2026 announcements",
        headers: ['Product', 'What it is', 'On sale', 'From'],
        rows: [
          ['iPhone 18 Pro', '6.3″, A20 Pro, smaller Dynamic Island', 'September 18, 2026', '$1,199'],
          ['iPhone 18 Pro Max', '6.9″, same internals, storage to 2 TB', 'September 18, 2026', '$1,299'],
          ['iPhone Duo', 'Foldable — 7.6″ inner / 5.4″ cover, Touch ID, eSIM-only', 'October 23, 2026', '$1,999'],
          ['Apple Watch Series 12', 'Annual refresh, display sizes carried over', 'September 18, 2026', '—'],
          ['Apple Watch Ultra 4', 'Large-display rugged watch, annual refresh', 'September 18, 2026', '—'],
          ['AirPods 5', 'New earbuds with Active Noise Cancellation', 'September 18, 2026', '—'],
        ],
      },
    },
    {
      heading: 'iPhone 18 Pro and 18 Pro Max: a marketing refresh, not a compliance one',
      body: [
        "The Pro keeps its 6.3″ display and the Pro Max its 6.9″ display — identical to the iPhone 17 Pro generation. That means the mandatory 6.9″ App Store screenshot slot is still 1320 × 2868 px, and every screenshot you already have for it stays valid. The visible changes are a smaller Dynamic Island (now toggling between three Live Activities), a slightly thicker camera plateau, and a new Burgundy finish alongside Black, Silver and Glacier. Under the glass: the A20 Pro chip, Apple's C2 modem, a variable-aperture main camera, and a 2 TB storage tier for the first time on iPhone, at $1,199 for the Pro and $1,299 for the Pro Max.",
        "None of that forces a re-export. What it does is date your mockup: a listing built around last year's handset — a wider Dynamic Island, last year's colours — reads as a beat behind to a shopper holding the new phone. Refreshing it is a conversion decision, not a Store requirement.",
      ],
      bullets: [
        'Nothing to re-upload: 6.3″ / 6.9″ unchanged, 1320 × 2868 px stays the 6.9″ standard.',
        'Update the device frame to the narrower Dynamic Island so the mockup matches the current hardware.',
        'Add a Burgundy device-colour variant if your brand leans warm — it is the standout new finish.',
        'A $1,199 entry price and a 2 TB storage tier raise the expectation that the listing itself looks premium.',
      ],
    },
    {
      heading: 'iPhone Duo: the one real change, and it now has a deadline',
      body: [
        "The iPhone Duo is Apple's first foldable: a book-style fold with a 7.6″ inner display at 1878 × 2670 pixels, opening from a 5.4″ cover display at 1398 × 2034 pixels. Apple's specifications put it at 5.2 mm thin open and 11.3 mm closed, with Touch ID in the side button instead of Face ID, an under-display FaceTime camera on the inner screen, and dual eSIM and no physical SIM tray at all. It shipped on October 23, 2026 in Night Sky and Star White at $1,999 for 256 GB, reaching 28 further countries and regions on October 30.",
        'Crucially for listings: at the event itself Apple published no store-asset requirement for the iPhone Duo, only developer sessions about making app UI resize fluidly. That changed on October 5, 2026, when Apple opened App Store submissions for iPhone Duo optimised apps and published screenshot specifications for the device — so there is now both a size to design against and a date to hit. The [dedicated iPhone Duo screenshot guide](/guides/iphone-18-iphone-ultra-app-store-screenshots) covers the design side; the next section has the sizes and the deadline.',
      ],
    },
    {
      heading: 'iPhone Duo screenshot sizes and the April 2027 deadline',
      body: [
        'App Store Connect lists iPhone Duo as its own entry in the screenshot specifications, with one set per display: 1398 × 2034 pixels (or 2034 × 1398 in landscape) for the outer display, and 2007 × 2853 pixels (or 2853 × 2007) for the inner display. Both are optional today, and Apple adds that starting in April 2027 screenshots will be required for any app using the iOS 27.1 SDK or later — which reads as any submission built on that SDK, optimised for the Duo or not.',
        "There is a reason to do it early rather than at the deadline: Apple says that when you submit iPhone Duo screenshots, your App Store product page displays a badge telling people the app is optimised for iPhone Duo. The rest of the checklist is short — recompile with Xcode 27.1, which adds iPhone Duo development support, check every pose and orientation in Device Hub, and preview how the assets land with App Store Connect's new product page preview tool. [Every other store screenshot size](/guides/app-store-screenshot-sizes) is untouched by the launch.",
      ],
      table: {
        caption: 'iPhone Duo App Store screenshot sizes',
        headers: ['Display', 'Portrait', 'Landscape', 'Required'],
        rows: [
          ['Outer display (5.4″)', '1398 × 2034 px', '2034 × 1398 px', 'Optional until April 2027'],
          ['Inner display (7.6″)', '2007 × 2853 px', '2853 × 2007 px', 'Optional until April 2027'],
        ],
      },
    },
    {
      heading: 'Apple Watch Series 12 and Ultra 4: a quick check, not a project',
      body: [
        'Both watches are annual refreshes and carry over their display sizes, so the watchOS screenshot sizes in App Store Connect are unchanged: Series 12 shares 416 × 496 pixels with Series 11 and Series 10, and Ultra 4 shares 422 × 514 pixels with Ultra 3. If you ship a watchOS app, the only thing worth doing is making sure your existing watch screenshots still reflect the current watchOS look; there is no new required dimension.',
        'Ecranify focuses on phone and tablet mockups, so watch screenshots are captured directly rather than framed in a 3D scene — but since nothing about the required sizes moved, that workflow is untouched.',
      ],
    },
    {
      heading: 'AirPods 5: nothing to upload, but the halo still matters',
      body: [
        "AirPods have no App Store listing of their own, so there is literally nothing to re-export. The indirect effect is on companion and audio apps whose screenshots feature earbuds: a hero frame showing last year's AirPods now reads as slightly dated. If that describes your listing, swap the product shot; if it does not, ignore this one entirely.",
      ],
    },
    {
      heading: 'The pattern: design from parametric sources, not flattened images',
      body: [
        'Every item on this list resolves to the same advice. If your screenshots are flattened PNGs exported from a design tool, each Apple event is a round of reopening files, nudging a device frame, retyping a headline and re-exporting at the right pixel size. If your screenshots are built from editable scenes, the same events are a five-minute re-export.',
        'In Ecranify a scene bundles the background, device, camera, lighting and every text layer as one editable unit, and devices are generated from parameters rather than modelled as fixed meshes. A colour change, a narrower Dynamic Island, or an entirely new folding body are all just adjusted values — the export presets and the composition stay put.',
      ],
    },
    {
      heading: 'Do this now in Ecranify',
      body: [
        'A short, concrete checklist so the September 2026 lineup costs you an afternoon rather than a redesign cycle.',
      ],
      bullets: [
        'Refresh your iPhone Pro Max mockup, swap in current screen captures, and re-export the 6.9″ slot at 1320 × 2868 px — the size did not change.',
        'Add a Burgundy device-colour variant of your key frames if it suits the brand.',
        'Build the iPhone Duo in the parametric device editor — 5.2 mm open profile, Touch ID side button, under-display camera, no SIM tray — then export the two Duo sets at custom resolutions of 1398 × 2034 and 2007 × 2853 pixels.',
        'Keep all screen captures raw, with no device chrome baked in, so the two iPhone Duo slots are a re-frame rather than a recapture — well before April 2027 if you ship on the iOS 27.1 SDK.',
        'Leave your watchOS screenshots as they are unless they look visually dated — no required size moved.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/iphone-6-9.jpg',
      alt: 'App Store screenshot at the iPhone 6.9-inch size with a 3D iPhone Pro Max mockup',
      caption: 'The 6.9″ slot the iPhone 18 Pro Max keeps using — still 1320 × 2868 px, still a preset in Ecranify.',
    },
    {
      src: '/marketing/examples/ipad-13.jpg',
      alt: 'App Store screenshot at the iPad 13-inch size showing a wide tablet layout',
      caption: "A tablet-shaped canvas is the nearest preset to the Duo's 2007 × 2853 px inner-display set.",
    },
    {
      src: '/marketing/examples/carousel.jpg',
      alt: 'Connected carousel screenshot strip spanning three App Store frames',
      caption: 'Carousel storytelling survives a device refresh when the layers stay separable.',
      wide: true,
    },
  ],
  faq: [
    {
      q: "Do I need new App Store screenshots after Apple's September 2026 event?",
      a: 'For the iPhone 18 Pro and Pro Max, no — the 6.3″ and 6.9″ display sizes are unchanged, so your existing sets stay valid. The iPhone Duo is the exception: Apple has published two screenshot sizes for it, 1398 × 2034 px for the outer display and 2007 × 2853 px for the inner display, optional today and required from April 2027 for apps submitted with the iOS 27.1 SDK or later.',
    },
    {
      q: 'What is the foldable iPhone called?',
      a: 'The iPhone Duo. Pre-announcement reporting mostly used "iPhone Ultra", and sometimes "iPhone Fold", but Apple named it the iPhone Duo on stage.',
    },
    {
      q: 'Is there an App Store screenshot size for the iPhone Duo?',
      a: 'Yes, two — one per display: 1398 × 2034 pixels for the outer display and 2007 × 2853 pixels for the inner display in portrait, with the same figures transposed for landscape. Apple published them alongside its October 2026 submission guidance, and they stay optional until April 2027.',
    },
    {
      q: 'Do I get anything for submitting iPhone Duo screenshots early?',
      a: 'Apple says that when you submit iPhone Duo screenshots in App Store Connect, your App Store product page displays a badge letting people know the app is optimised for iPhone Duo. That is available now, rather than at the April 2027 deadline.',
    },
    {
      q: 'When do the new devices ship?',
      a: 'The iPhone 18 Pro, iPhone 18 Pro Max, Apple Watch Series 12, Apple Watch Ultra 4 and AirPods 5 went on sale September 18, 2026. The iPhone Duo went on sale October 23, 2026, with pre-orders from October 16 and 28 further countries and regions from October 30.',
    },
    {
      q: 'Does the smaller Dynamic Island change my screenshots?',
      a: 'No. It is a detail in the device frame, not a change to the screenshot canvas size. Update the mockup if you want it to look current, but nothing about the required dimensions moved.',
    },
    {
      q: 'Can I mock up the iPhone Duo before I own one?',
      a: 'Yes. Ecranify builds every device from parameters — body dimensions, corner radius, bevel, front-face inset, camera and cutout placement, buttons and decals — so you can assemble the unfolded body from published specs without waiting for a template.',
    },
  ],
  cta: {
    title: 'One lineup, one afternoon',
    body: 'Refresh your iPhone Pro Max frames and build the iPhone Duo body from parameters in the same editor — with App Store and Google Play export presets built in. Free to start.',
  },
  ctaSecondaryLinks: [
    {to: '/guides/iphone-18-iphone-ultra-app-store-screenshots', label: 'Deep dive: iPhone Duo screenshots'},
    {to: '/guides/app-store-screenshot-sizes', label: 'All App Store screenshot sizes'},
  ],
}

// ── Localized screenshots for every device, without redesigning ────

const localizedScreenshots: GuideData = {
  slug: 'localized-app-store-screenshots',
  canonicalPath: '/guides/localized-app-store-screenshots',
  title: 'Localized App Store Screenshots for Every Device, Without Redesigning Each One | Ecranify',
  description:
    'How to ship App Store and Google Play screenshots for every language and device size without redesigning each one — a worked example with a real app, plus a Maestro-based capture pipeline.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'Localized screenshots for every device, without redesigning a single one',
  intro: [
    'Supporting a new market is rarely just a translated app description. Every screenshot slot each store requires has to exist again for every language, and every language has to exist again for every screen you want to show. A modest app with four languages, five feature screens and two device classes (phone and tablet) is forty finished images — before anything changes about the app itself.',
    'Done by hand, that math is exactly why localized listings get skipped: a designer has to reopen the same composition forty times, swap a screen capture, retype a headline, and re-export at the right pixel size, hoping nothing drifts between the first version and the last. This guide walks through a different approach — build the design once as a reusable scene, then treat language and device as data you swap in, not art you redraw — using a real published app, the word-and-number puzzle app Crosswords & Puzzles, as the worked example, plus a Maestro-based pipeline that keeps the source screen captures from ever going stale.',
  ],
  updated: 'October 2026',
  readingTime: '10 min read',
  sections: [
    {
      heading: 'Why localized screenshots convert — the data',
      body: [
        'This is not a cosmetic nicety. Screenshot localization is one of the highest-leverage ASO activities measured: AppTweak data shows full page localization — visuals included, not just translated captions — driving up to 38% more downloads in non-English markets, and industry analyses put the typical range at 15–40%, with some apps seeing gains as high as 200% in specific markets after localizing.',
        'The size of the gain depends heavily on how far the localization actually goes. Translating captions word-for-word into Spanish has been measured at a 4–8% lift in Spanish-speaking markets, while genuinely localizing the imagery, on-screen text and cultural framing gets 18–26% — roughly triple. ZiMAD saw a 36% conversion increase localizing Magic Jigsaw Puzzles for Japan, where players responded to a visibly different composition style than the Western original. Even without touching a single language, adding clear on-screen captions to otherwise bare screenshots is separately measured at a 20–35% conversion lift. The two effects compound: caption text does the work, and it has to be in the shopper’s language to do it.',
      ],
    },
    {
      heading: 'The trick: one scene, two things change',
      body: [
        'A screenshot composition is made of layers that mostly should not vary between languages or devices: the background, the lighting, the device color and material, the camera angle, and the position and size of the headline. What has to vary is almost always exactly two things — which screen capture is loaded onto the device, and what the headline text says.',
        'In Ecranify, a scene bundles all of it — background, device, camera, lighting, and every text and sprite layer — as one editable unit. Duplicating a finished scene tab and only touching the screen image and the headline string keeps everything else identical by construction. There is no risk of the second language subtly drifting in device angle or background tone from the first, because it is not a new composition — it is the same one with two swapped inputs. Template tools tackle the same problem from the other side: AppLaunchpad, for example, builds localization into its templates, as our [AppLaunchpad alternative comparison](/alternatives/applaunchpad) describes.',
      ],
    },
    {
      heading: 'Worked example: Crosswords & Puzzles, one template, four languages, two device classes',
      body: [
        'Crosswords & Puzzles ships in Slovak, Czech, English and German and needs App Store screenshots for both iPhone and iPad. Five screens tell the story: the app menu, word search, crosswords, sudoku and nonograms. Multiplied out — 4 languages × 5 screens × 2 device classes — that is 40 finished images per store update, and every one of them uses the same warm, linen-textured background and the same silver iPhone / iPad mockup so the whole gallery reads as one listing rather than forty separate exports.',
        'Two of the five screens — sudoku and nonograms — carry no on-screen text at all, so they need only one asset per device size instead of one per language: a free four-way cut in the total. The menu, word search and crossword screens do carry text, so those are the ones actually duplicated per language, with only the headline and the captured screen swapped each time.',
      ],
    },
    {
      heading: 'Same scene, translated text: what it looks like in practice',
      body: [
        'The screenshots below are different scene tabs in the same Ecranify project — same background photo, same device finish, same headline position, size and color. Between the three phone versions, the only edits are the screen capture loaded onto the device and the two lines of headline copy, translated per market rather than transliterated. The iPad versions underneath change one more thing — the device itself — while keeping the same background, lighting and text styling, so the phone and tablet listings read as one coherent set instead of two unrelated ones.',
      ],
    },
    {
      heading: 'Make the device match the app, not a generic template',
      body: [
        'A device mockup is not neutral set dressing — its color and the background around it either reinforce the app or fight it. Crosswords & Puzzles has a handwritten, paper-and-pencil visual identity, so the scene uses a warm linen background with coffee-ring texture and a rose-gold/silver aluminum iPhone rather than the dark, glossy, tech-blue look that is the default reach for most screenshot tools. Getting that pairing right once, in the scene that becomes the template, is what makes forty exports look designed instead of assembled.',
        'Because device color, background image and lighting all live on the same scene object as the text and screen capture, this decision only has to be made once per app — it then rides along automatically into every language and every device duplicate made from that scene.',
      ],
    },
    {
      heading: 'The same reuse pattern for a second device class',
      body: [
        'Adding a device class works the same way as adding a language. Duplicating a finished iPhone scene into an iPad-sized export preset carries the background, device finish, lighting and text styling forward untouched, so “now on iPad too” is a resize and a fresh screen capture, not a redesign — the same iPad versions of the Crosswords & Puzzles menu shown further down were produced this way, one duplicated scene per language.',
      ],
    },
    {
      heading: 'Automating the screen captures so the source images never go stale',
      body: [
        'The step that actually breaks this workflow in practice is not the design — it is keeping forty screen captures current every time the app’s UI changes. Recapturing that many screens by hand, in four languages, on two device classes, before every release, does not survive contact with a real release schedule. The fix is a small, deterministic capture pipeline built with Maestro, an open-source YAML-based mobile UI automation tool that drives a real app the same way a person would tap through it. Some Mac-only screenshot apps capture straight from the Xcode Simulator instead, as our [ButterKit alternative comparison](/alternatives/butterkit) describes; a Maestro flow works the same for iOS and Android.',
        'Two pieces make it reliable rather than fragile. First, a dedicated "screenshot mode" flag baked into a release build: when it is on, each screen serves a fixed, pre-filled state (a solved-looking board, deterministic content) instead of a randomly generated one, so a rerun produces byte-identical output and there is nothing to re-check by eye. Second, every tap in the automation targets a stable element ID rather than on-screen text — so the exact same flow drives the menu whether it is currently showing Slovak, Czech, English or German, and does not need one script per language.',
      ],
      table: {
        caption: 'The Maestro capture flow, in outline',
        headers: ['Step', 'What it does', 'Why it is built that way'],
        rows: [
          [
            'Launch + screenshot mode',
            'Launches the release build with a screenshot-mode flag baked in at build time',
            'Every screen renders fixed, pre-filled content — no flaky random data to catch mid-shot',
          ],
          [
            'Capture language-neutral screens once',
            'Navigates to screens with no on-screen text (e.g. a number puzzle) and shoots them a single time',
            'A screen with nothing to translate does not need a copy per language',
          ],
          [
            'Loop a per-language sub-flow',
            'Re-runs one flow file once per supported language, passing the language as a parameter',
            'Navigation is by stable element ID, so one flow file drives every language',
          ],
          [
            'Set the language, then capture',
            'Inside the loop: switch the in-app language in settings, then shoot the menu and each localized screen',
            'The screenshot mode’s fixed board means the layout is identical run to run, only the strings change',
          ],
          [
            'Collect the output',
            'A small script gathers the freshly written images from the test run into one predictable folder',
            'Gives every downstream step (including the Ecranify scenes) a fixed, predictable filename to point at',
          ],
        ],
      },
    },
    {
      heading: 'From a fresh set of PNGs to a re-exported, localized listing',
      body: [
        'Once the capture pipeline names its output consistently — one file per language and screen — updating the store listing stops being a design task and becomes a swap. Each Ecranify scene tab corresponds to one language-and-screen pair, so a refreshed capture drops straight into that scene’s screen-image slot; the headline text for a given language is typed once and does not need retyping on the next release unless the copy itself changes. Re-exporting the whole set then runs through the same per-scene export-resolution preset — iPhone 6.9″, iPad 13″, or a Google Play size — so a UI change that touches all forty images is a batch of drop-ins and one export pass, not forty redesigns.',
      ],
    },
    {
      heading: 'A checklist for doing this on your own app',
      body: [
        'The pattern generalizes past this one example. Working through it in this order avoids most of the rework:',
      ],
      bullets: [
        'Design one scene per feature screen first — background, device color and lighting you are happy to commit to — before touching languages at all.',
        'Give your app a deterministic "screenshot mode" so a recapture is byte-identical, not a fresh set of random content to re-check.',
        'Navigate to each screen by a stable element ID in your capture automation, not by the on-screen label, so one flow drives every language.',
        'Localize captions, do not just translate them — a benefit-led rewrite in the target language consistently outperforms a literal translation.',
        'Duplicate a finished scene for every new language or device class instead of rebuilding the composition — background, device and text styling should carry over untouched.',
        'Automate the capture step so a UI change refreshes the source images on demand, instead of a person re-shooting every locale by hand before each release.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/crosswords-editor-flow.jpg',
      alt: 'Ecranify editor showing scene tabs for the same screenshot design duplicated across Slovak, English and Czech',
      caption: 'One design, duplicated per language — each tab only swaps the screen capture and headline text.',
      wide: true,
    },
    {
      src: '/marketing/examples/crosswords-sk.jpg',
      alt: 'Crosswords & Puzzles App Store menu screenshot in Slovak on an iPhone mockup',
      caption: 'Slovak — same background, device and layout as every other language.',
    },
    {
      src: '/marketing/examples/crosswords-de.jpg',
      alt: 'Crosswords & Puzzles App Store menu screenshot in German on an iPhone mockup',
      caption: 'German — the same menu screen, only the screen capture and headline changed.',
    },
    {
      src: '/marketing/examples/crosswords-en.jpg',
      alt: 'Crosswords & Puzzles App Store menu screenshot in English on an iPhone mockup',
      caption: 'English — the same menu screen, one more time.',
    },
    {
      src: '/marketing/examples/crosswords-sk-ipad.jpg',
      alt: 'Crosswords & Puzzles App Store menu screenshot in Slovak on an iPad mockup',
      caption: 'The same scene, duplicated for iPad — Slovak.',
    },
    {
      src: '/marketing/examples/crosswords-de-ipad.jpg',
      alt: 'Crosswords & Puzzles App Store menu screenshot in German on an iPad mockup',
      caption: 'The same scene, duplicated for iPad — German.',
    },
    {
      src: '/marketing/examples/crosswords-en-ipad.jpg',
      alt: 'Crosswords & Puzzles App Store menu screenshot in English on an iPad mockup',
      caption: 'The same scene, duplicated for iPad — English.',
    },
  ],
  faq: [
    {
      q: 'How many App Store screenshots do I need if I support multiple languages?',
      a: 'Both stores require the full screenshot set to exist per localization, not just a translated description. Multiply your device sizes (e.g. iPhone 6.9″, iPad 13″) by your number of supported languages by the number of feature screens you show — a small app with a few languages and screens easily reaches 30–40 images.',
    },
    {
      q: 'Is translating the captions enough, or do I need to fully localize?',
      a: 'Translation alone measurably underperforms real localization. Studies put word-for-word caption translation at a 4–8% conversion lift versus localized imagery and copy at 18–26% — worth the extra effort if the market matters to you.',
    },
    {
      q: 'Can I reuse the same background and device mockup across every language?',
      a: 'Yes — that is the point. The background, device color, lighting and text position should stay identical across languages; only the screen capture and the translated text need to change. Reusing one scene per screen guarantees that consistency automatically.',
    },
    {
      q: 'What is Maestro, and do I need it to do this?',
      a: 'Maestro is an open-source, YAML-based mobile UI automation tool. It is optional — you can capture screenshots manually — but for more than a couple of languages or screens, automating the capture is what keeps the source images current without a person re-shooting every locale before each release.',
    },
    {
      q: 'Do screens with no on-screen text still need a version per language?',
      a: 'No. A screen that shows no translatable text (like a number puzzle) needs only one capture per device size, not one per language — a useful shortcut when planning how many assets a listing actually needs.',
    },
    {
      q: 'Does this work for Google Play as well as the App Store?',
      a: 'Yes. Google Play screenshots are localized per language the same way App Store screenshots are, and Ecranify exports both stores’ required sizes from the same scene.',
    },
  ],
  cta: {
    title: 'Turn one scene into every language and device you support',
    body: 'Build the composition once in Ecranify, then duplicate the scene per language and device class — background, lighting and device stay locked while you swap the capture and the copy. Free to start.',
  },
}

// ── Case study: +80% downloads from new screenshots + device support ──

const screenshotCaseStudy: GuideData = {
  slug: 'screenshot-case-study-80-percent-more-downloads',
  canonicalPath: '/guides/screenshot-case-study-80-percent-more-downloads',
  title: 'Case Study: New Screenshots Drove 80% More Downloads in 13 Weeks | Ecranify',
  description:
    'A real App Store Connect case study: adding iPhone/iPad support and shipping a new Ecranify screenshot set — including a connected carousel — for a paid Apple Watch game drove an 80% download increase in 13 weeks, with no other marketing running.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Case study',
  h1: 'New devices, new screenshots, zero marketing: 80% more downloads in 13 weeks',
  intro: [
    'This is a small, real number from App Store Connect, not a projection or an agency case study written to sell you something: over a 13-week window, downloads for a tiny paid Apple Watch game rose 80% after exactly two changes — the app gained iPhone and iPad support, and its App Store screenshots were rebuilt from scratch in Ecranify. Nothing else about the app’s marketing changed in that window, which is what makes the number worth writing up: there is no ad spend, no press hit and no influencer post competing for credit.',
    'We will get to the actual figures shortly, and — full disclosure — they are small enough that you could plausibly print the download list on an index card. That is the point, not a caveat: the mechanism behind an 80% jump does not care whether the starting line is 9 downloads a week or 9,000.',
  ],
  updated: 'October 2026',
  readingTime: '7 min read',
  sections: [
    {
      heading: 'The app: a paid nostalgia trip, built first for Apple Watch',
      body: [
        'Space Impact Watch is a faithful little remake of Space Impact, the missile-dodging shooter bundled with Nokia 3310-era phones, rendered in the same chunky green monochrome LCD look. It is a paid download — no ads, no in-app purchase funnel to fall back on if the store listing does not sell the app — and it was originally built exclusively for Apple Watch, a screen almost no screenshot tool or store-optimization guide pays attention to.',
        'A paid app with no free tier and no algorithmic feature placement has essentially one sales pitch available to it: the product page. Nobody who lands there has been softened up by a free trial. Whatever convinces them has to happen in [the app icon](/guides/app-store-icon-guidelines), the screenshots, and the first line of the description — which makes this app an unusually clean test bed for how much the screenshots themselves are actually doing.',
      ],
    },
    {
      heading: 'What actually changed — two things, nothing else',
      body: [
        'Two changes shipped together. First, native iPhone and iPad support: a game that had only ever been playable on a Watch became playable on the two devices that essentially everyone browsing the App Store is holding. That alone widens who the listing is even relevant to — you stop pitching “a Watch app” to the sliver of shoppers filtering by Watch compatibility and start pitching “a game” to anyone scrolling on their phone.',
        'Second, the entire screenshot set was rebuilt in Ecranify to actually show that. The same warm, pixel-green Nokia aesthetic was carried over onto 3D iPhone and iPad mockups, and — because the composition allowed it — a connected two-frame carousel was built where the phone visually runs off the right edge of the first screenshot and continues into the second, the kind of alignment that is fiddly to pull off by hand and exactly what a carousel export exists to automate.',
      ],
    },
    {
      heading: 'What that looked like in the editor',
      body: [
        'One Ecranify project holds every version needed for the update: a single iPhone screenshot, a single iPad screenshot, and a two-frame carousel for each, all sharing the same background, device finish and headline styling so the whole set reads as one coherent listing rather than a pile of separate exports.',
      ],
    },
    {
      heading: 'The result, in App Store Connect’s own numbers',
      body: [
        'App Store Connect’s Downloads chart for the 13-week window from May 25 to August 23, 2026 shows 119 total downloads, a weekly average of 9, and an 80% increase over the prior comparable period. The chart itself marks the exact week the update — iPhone/iPad support plus the new screenshots — went live, and every week after that point sits visibly above every week before it; the step is not a gradual drift, it lines up with the release.',
        'Let’s be honest about scale: 119 downloads over three months will not fund a sequel, and a 9-per-week average is the kind of number where you could plausibly recognize repeat visitors. But imagine the same shape on an app with a mainstream baseline — a title pulling a few thousand downloads a week instead of nine. The identical 80% step would read as a genuinely large growth event, the kind that gets a screenshot of its own in an investor update. The absolute numbers here are just this particular app being extremely, deliberately niche. The mechanism producing the 80% is the interesting part, and it does not scale with audience size.',
      ],
    },
    {
      heading: 'Why it is fair to credit the listing, not something else',
      body: [
        'The reason this number is usable at all is what did not happen in the same window: no paid user acquisition ran, no press or blog coverage picked the app up, no creator or community post drove a traffic spike, Apple did not feature it, and the price did not change. The only content update in the release was the device support the new screenshots exist to communicate — there is no second variable competing for credit.',
        'App Store discovery for an unfeatured paid app is still overwhelmingly search and browse, and for both, the product page — icon, screenshots, description — is the entire pitch a shopper sees before deciding. With every other lever untouched, the two changes above are the only credible explanation for a step change that lines up exactly with their release date.',
      ],
    },
    {
      heading: 'What made the new screenshots different',
      body: [
        'The design choices were small but consistent. A black background makes the green LCD screen the only thing your eye can land on — there is nothing else in frame competing with it. The device frame stayed on-brand rather than generic: the same pixel typeface used in the game’s own UI carries into the headline text, so the screenshot looks like an extension of the app rather than a marketing template dropped on top of it. And the connected carousel — the Nokia phone bleeding off the edge of frame one and reappearing in frame two next to the Apple Watch it started on — visually tells the “also on iPhone, also on iPad” story in a way a caption alone would have to spell out.',
        'None of this required new photography or a designer starting from a blank canvas per device: the iPad screenshot is the iPhone composition duplicated into a bigger frame, and the carousel is the single-frame composition sliced across two store-ready exports by Ecranify’s carousel export rather than laid out frame-by-frame by hand.',
      ],
    },
    {
      heading: 'The takeaway for an app with a marketing budget of zero',
      body: [
        'Most small apps do not have a press contact or an ad budget to test with — the store listing is one of the only levers fully in your control that a shopper actually sees before they decide, and industry research consistently puts screenshots as the single biggest factor in that decision. If your app has a natural next platform (a Watch app that could run on the phone, a phone app that could run on a tablet), widening device support and refreshing the screenshots to actually show it are cheap enough to ship together and honest enough to attribute afterward. Once a listing has steady traffic, a [screenshot A/B test in App Store Connect or Play Console](/guides/app-store-screenshot-ab-testing) gives a cleaner answer than a before-and-after comparison.',
      ],
      bullets: [
        'If there is a natural next platform for your app, ship it — it widens who the listing is relevant to before a single pixel of the screenshots changes.',
        'Refresh the screenshot set the moment device support changes; a listing that only shows the old platform undersells what you just built.',
        'Use a connected carousel where the composition allows it — it reads as more deliberate and polished without any new photography.',
        'Ship the device update and the new screenshots together, then hold everything else steady for a few weeks so you get one clean before/after step instead of a blur of causes.',
        'Write down what else did or did not happen in that window (ads, press, features, price) — that record is the only reason a result like this is attributable instead of a guess.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/space-impact-editor-flow.jpg',
      alt: 'Ecranify editor showing Space Impact Watch scenes for iPhone single, iPhone carousel, iPad single and iPad carousel',
      caption: 'One Ecranify project, every export the update needed: iPhone, iPad, and a connected carousel for each.',
      wide: true,
    },
    {
      src: '/marketing/examples/space-impact-downloads-chart.jpg',
      alt: 'App Store Connect downloads chart showing an 80 percent increase over 13 weeks after a new release',
      caption: 'App Store Connect, May 25 – Aug 23, 2026: 119 downloads, +80%, stepping up right at the release.',
      wide: true,
    },
    {
      src: '/marketing/examples/space-impact-iphone.jpg',
      alt: 'Space Impact Watch App Store screenshot on a 3D iPhone mockup',
      caption: 'The iPhone screenshot — new device support, shown rather than just claimed.',
    },
    {
      src: '/marketing/examples/space-impact-carousel.jpg',
      alt: 'Space Impact Watch connected two-frame carousel screenshot, shown side by side',
      caption: 'The connected carousel — the phone runs off the right edge of frame 1 and continues into frame 2.',
      wide: true,
    },
  ],
  faq: [
    {
      q: 'What exactly changed on Space Impact Watch’s App Store listing?',
      a: 'Two things, shipped together: the app gained native iPhone and iPad support (it was previously Apple Watch-only), and the full App Store screenshot set was rebuilt in Ecranify to show the new devices, including a connected two-frame carousel.',
    },
    {
      q: 'Is an 80% increase in downloads a big deal if the total is only 119?',
      a: 'In absolute terms, no — this is a deliberately niche paid app. What makes the number worth studying is the mechanism: the same relative step, on an app with a mainstream download baseline, would represent a genuinely large growth event. The percentage is the signal; the total is just this app’s scale.',
    },
    {
      q: 'How do you know the screenshots caused the increase, and not something else?',
      a: 'No other marketing ran in the same window — no paid ads, no press coverage, no creator or community posts, no App Store feature placement, and no price change. With every other lever held constant, the device-support release and its new screenshots are the only variables left to explain a step change that lines up with the release date.',
    },
    {
      q: 'Do I need a connected carousel screenshot for this to work?',
      a: 'No — a single well-composed screenshot per device already communicates far more than raw UI. The carousel was used here because the composition supported it (a phone visually continuing across frames); it is a polish detail, not a requirement.',
    },
    {
      q: 'Does this apply to free apps too, or only paid ones?',
      a: 'The mechanism applies to both — the App Store product page is what a shopper evaluates before installing either way. It is easier to isolate for a paid app with no ads and no viral loop, which is why this case study is a clean read; a free app with other acquisition channels running would need the same “nothing else changed” discipline to attribute a result this cleanly.',
    },
    {
      q: 'Can Ecranify make a carousel screenshot like this one?',
      a: 'Yes. Ecranify’s carousel export composes a wide composition and slices it into individual store-ready frames automatically, so a device (or in this case, a phone bleeding into the next frame) can visually continue across a multi-screenshot set without manually aligning each export.',
    },
  ],
  cta: {
    title: 'Turn a platform launch into a listing that matches it',
    body: 'When your app gains a new device, Ecranify lets you duplicate the scene you already have instead of starting over — same background, same device finish, a new export size. Free to start.',
  },
}

// ── New free tools: image converter + video to GIF ──────────────────

const freeTools: GuideData = {
  slug: 'free-image-converter-video-to-gif',
  canonicalPath: '/guides/free-image-converter-video-to-gif',
  title: 'New Free Tools: Image Format Converter & Video to GIF | Ecranify',
  description:
    'Two new free browser tools: convert images between PNG, JPEG, WebP, GIF, BMP and ICO with a live preview and crop tool, or turn a video clip into an optimized, shareable GIF.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'Two new free tools: an image format converter and a video-to-GIF converter',
  intro: [
    'Alongside the App Store screenshot editor, the icon generator and the QR code generator, Ecranify now has two more free tools: an image format converter and a video-to-GIF converter. Both are free, need no signup, and live at ecranify.com/tools with the rest.',
    'Neither is a toy demo bolted onto the site — the image converter has a real interactive preview (its own drop zone, a live simulation of JPEG/WebP compression as you drag the quality slider, a crop tool, an exact output-size estimate) and the video converter runs the actual conversion on a server rather than in your browser, so a long clip on an old phone converts as reliably as a short one on a desktop.',
  ],
  updated: 'August 2026',
  readingTime: '5 min read',
  sections: [
    {
      heading: 'Image converter: which format actually fits your use case',
      body: [
        'PNG, JPEG, WebP, GIF, BMP and ICO all solve slightly different problems, and picking the wrong one is a common, avoidable source of bloated page weight or missing transparency. The converter handles all six, both directions, entirely in the browser — nothing is uploaded anywhere.',
      ],
      bullets: [
        'PNG — lossless, supports transparency. Use it for logos, icons, screenshots with UI chrome, or anything that gets edited again later.',
        'JPEG — lossy, no transparency, smallest files for photos. Use it for photographic content where a little compression is invisible but file size matters (email attachments, photo galleries).',
        'WebP — modern, usually smaller than both PNG and JPEG at the same visual quality, with optional transparency. The right default for web images if you don’t need maximum compatibility with very old software.',
        'BMP — uncompressed, large, universally readable by the oldest software. Rarely the right choice today; mostly needed for legacy tooling or Windows-specific workflows that expect it.',
        'ICO — the Windows icon container format, supports several sizes in one file. Needed specifically for Windows app/favicon icons; not a general-purpose image format.',
      ],
    },
    {
      heading: 'A preview that actually shows you the result',
      body: [
        'Dragging the quality slider on a JPEG or WebP export re-encodes the image through the real browser codec and redraws the decoded result — so what you see is what you get, compression artifacts included, not a flat draw that ignores the slider. An exact output file size is computed the same way, from the real encoder, not a guess.',
        'The preview is also a drop zone in its own right, and includes a crop ("trim") tool with draggable corner handles for cutting an image down before converting it — useful for stripping unwanted borders or focusing on one part of a larger source image without leaving the tool.',
      ],
    },
    {
      heading: 'Video to GIF: why GIF still matters in 2026',
      body: [
        'A GIF autoplays everywhere a video usually can’t — inline in a Slack message, a forum post, a README, a tweet, an email — with no player controls, no tap-to-play, no format-compatibility question. That single property (silent, looping, autoplaying, universally supported) is why short-form clips still get converted to GIF constantly, video codecs aside.',
        'Upload a clip, pick a start and end point, choose a resolution and frame rate, and the conversion runs server-side with a real ffmpeg encode — including the standard two-pass palette-generation technique that keeps a converted GIF from banding or looking muddy, which a naive conversion skips.',
      ],
    },
    {
      heading: 'Where these fit if you’re shipping an app',
      body: [
        'Both tools are useful well outside app marketing, but they line up with it too: convert a design export between formats before it goes into an App Store listing, turn your app icon source art into the exact ICO a Windows build needs, or clip a short screen recording into a GIF preview for a landing page, changelog or investor update — something an App Store screenshot preset isn’t built for, and a full video export is overkill for.',
      ],
    },
  ],
  screenshots: [],
  faq: [
    {
      q: 'Do I need an account to use the image converter or the video-to-GIF tool?',
      a: 'No — both are free and need no signup. The image converter never uploads anything; conversion happens entirely in your browser. The video-to-GIF tool does upload the video (conversion runs on our server for reliability), and the converted file is deleted automatically a few minutes after you download it.',
    },
    {
      q: 'What image formats are supported?',
      a: 'PNG, JPEG, WebP, GIF (static output), BMP and ICO, converting between any of them. Animated GIF input is read as its first frame only. AVIF and TIFF are not supported yet.',
    },
    {
      q: 'Is there a size or length limit on the video-to-GIF converter?',
      a: 'Yes — uploads are capped and the processed segment length, output resolution and frame rate are all bounded so a conversion finishes in a reasonable time on shared server capacity. The tool’s trim and settings controls stay within those limits.',
    },
    {
      q: 'Why does the image converter show a different file size than what I actually download?',
      a: 'It shouldn’t — the estimate is computed by running the real converter in the background for your current format and quality settings, the same code path the actual download uses, so it should match exactly (rounded for display).',
    },
    {
      q: 'Where can I find all of Ecranify’s free tools?',
      a: 'They’re all listed at ecranify.com/tools — the icon generator, QR code generator, image converter and video-to-GIF converter.',
    },
  ],
  cta: {
    title: 'Free tools, and a free 3D mockup editor to go with them',
    body: 'Convert an image or a video clip, then turn your screenshots into photorealistic App Store and Google Play mockups in the same free editor. No signup, pay only when you export.',
  },
  ctaSecondaryLinks: [
    {to: '/image-converter', label: 'Free image converter'},
    {to: '/video-to-gif', label: 'Free video to GIF'},
  ],
}

// ── App Store screenshot rejected: causes and fixes ─────────────────

const screenshotRejections: GuideData = {
  slug: 'app-store-screenshot-rejection-reasons',
  canonicalPath: '/guides/app-store-screenshot-rejection-reasons',
  title: 'App Store Screenshot Rejected? Guidelines & Fixes | Ecranify',
  description:
    'App Store screenshot rejected? The upload errors and screenshot guidelines behind it — size, alpha, 2.3.2, 2.3.3, 2.3.10 — and how to resubmit the same build.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'App Store screenshot rejected? The causes and how to fix each one',
  intro: [
    'An App Store screenshot gets rejected for one of two reasons. Either App Store Connect refuses the file at upload — wrong pixel size, an alpha channel, an unsupported format or too many images — or App Review rejects the listing because what the screenshot shows breaks a guideline, most often 2.3.3 (screenshots must show the app in use) or 2.3.10 (no imagery of other mobile platforms).',
    'The first kind is fixed by re-exporting the file correctly; the second by changing what the image shows. Screenshots are metadata, so after a metadata rejection you can upload new images and resubmit the same build. This guide lists every common cause, the exact fix, and a checklist to run before the next upload.',
  ],
  updated: 'October 2026',
  readingTime: '8 min read',
  sections: [
    {
      heading: 'Upload error or App Review rejection? Tell them apart first',
      body: [
        'An upload error happens before anyone at Apple looks at your listing: App Store Connect validates the file itself and refuses to accept it. Nothing is under review yet, so there is nothing to appeal — the file simply has to meet the specification.',
        'An App Review rejection arrives later, as a message that names the guideline your submission breaks. The images uploaded fine; a reviewer decided their content is not acceptable. Here the fix is editorial, and you can reply to App Review if you believe the reviewer misread a screenshot.',
        'Rejections are common, so one is no reason to panic. Apple’s 2025 App Store Transparency Report counts 9,100,620 app submissions reviewed and 2,093,244 rejected. Performance — the guideline section that contains the 2.3 Accurate Metadata rules for screenshots — is cited in 1,354,418 rejections, more than any other section.',
      ],
    },
    {
      heading: 'Why App Store Connect refuses a screenshot file',
      body: [
        'Apple’s specification is short: you can upload one to 10 screenshots per device size and localization, as .jpeg, .jpg or .png, and images can’t include alpha channels or transparencies — the same no-alpha rule applies to the 1024 × 1024 app icon, as our [App Store icon requirements guide](/guides/app-store-icon-guidelines) explains. Almost every upload failure is one of these four:',
      ],
      bullets: [
        'Wrong pixel size — the image must match one of the accepted sizes exactly. A screenshot resized by a few pixels, or exported at 2x from a design tool, is refused.',
        'Alpha channel — many design tools write PNGs with an alpha channel even when nothing is transparent. Export as JPEG, or flatten the image onto an opaque background before exporting as PNG.',
        'Unsupported format — HEIC, WebP or PDF files have to be converted to PNG or JPEG first.',
        'Too many images — more than 10 screenshots for one device size in one localization.',
      ],
    },
    {
      heading: 'App Store screenshot sizes that App Store Connect accepts',
      body: [
        'If your app runs on iPhone, you need the 6.9″ set, or the 6.5″ set when you don’t provide 6.9″ screenshots; smaller iPhones use scaled versions of what you upload. If it runs on iPad, the 13″ set is required. These are the accepted sizes for the required slots, from Apple’s screenshot specifications — the optional sizes for every other device are in our [App Store and Google Play screenshot sizes guide](/guides/app-store-screenshot-sizes).',
      ],
      table: {
        caption: 'Accepted sizes for the required App Store screenshot slots (October 2026)',
        headers: ['Display', 'Portrait (px)', 'Landscape (px)', 'Required'],
        rows: [
          [
            'iPhone 6.9″',
            '1320 × 2868, 1290 × 2796 or 1260 × 2736',
            '2868 × 1320, 2796 × 1290 or 2736 × 1260',
            'If the app runs on iPhone',
          ],
          [
            'iPhone 6.5″',
            '1284 × 2778 or 1242 × 2688',
            '2778 × 1284 or 2688 × 1242',
            'Only if no 6.9″ set is provided',
          ],
          ['iPad 13″', '2064 × 2752 or 2048 × 2732', '2752 × 2064 or 2732 × 2048', 'If the app runs on iPad'],
        ],
      },
    },
    {
      heading: 'App Store screenshot guidelines that cause rejections',
      body: [
        'Section 2.3 of the App Review Guidelines asks that screenshots and previews accurately reflect the app’s core experience and stay up to date with new versions. These are the guidelines screenshot rejections cite most often:',
      ],
      table: {
        caption: 'App Review Guidelines that apply to screenshots',
        headers: ['Guideline', 'What gets flagged', 'How to fix it'],
        rows: [
          [
            '2.3.3 Screenshots',
            'Only title art, a login page or a splash screen instead of the app in use',
            'Show real screens of the app doing its main job; text and image overlays are allowed',
          ],
          [
            '2.3.10 Platform-specific metadata',
            'Names, icons or imagery of other mobile platforms, e.g. an Android phone frame',
            'Use Apple device frames or no frame for the App Store set',
          ],
          [
            '2.3.2 In-app purchases',
            'Paid items, levels or subscriptions shown as if they were included in the download',
            'Make clear in the caption which shown features need an additional purchase',
          ],
          [
            '2.3.7 Metadata',
            'Prices, terms or descriptions baked into the images',
            'Remove prices and promotional terms from captions',
          ],
          [
            '2.3.8 Age-appropriate metadata',
            'Images that would not fit a 4+ rating, even if the app is rated higher',
            'Pick screens without graphic content',
          ],
          [
            '2.3 Accurate metadata',
            'Screens or features that the submitted build does not have',
            'Only show what a reviewer can find in the build',
          ],
          [
            '2.2 Beta testing',
            'Beta or demo labels, test data and debug overlays',
            'Capture screens from a release build with realistic content',
          ],
        ],
      },
    },
    {
      heading: 'Device frames, captions and the other-platform trap',
      body: [
        'Framed screenshots with headlines are fine: guideline 2.3.3 explicitly allows text and image overlays. The rule to watch is 2.3.10. A shared screenshot set with a Pixel or Galaxy frame shows another mobile platform, so build one set on Apple devices for the App Store and a separate set for Google Play.',
        'In the [free Ecranify editor](/free) that is a device switch, not a redesign: pick the iPhone 18 Pro Max for the App Store scene and the Pixel 10 Pro XL for the Play version, keep the same screen image and background, and export each with its store preset. The editor is free to use; you pay only when you export.',
        'Keep captions about what the app does: a caption like “50% off this week” is a price claim under 2.3.7.',
      ],
    },
    {
      heading: 'Localizations and device sizes: where a flagged screenshot hides',
      body: [
        'App Store Connect keeps a separate screenshot set for each device size and each localization. If your interface is the same across sizes and languages, Apple only needs the highest required resolution and scales it down for smaller devices; anything else is added per size and per language in Media Manager (View All Sizes in Media Manager on the app page).',
        'That is why a fix can miss: the reviewer cites one image, but the same file sits in several localizations or in an extra device size. Before resubmitting, open Media Manager for every language you support and check each device tab. Guideline 2.3 also expects screenshots to stay up to date with new versions, so an old set that still shows a removed screen can be flagged on a later update.',
        'Google Play checks different things — phone screenshots there can be any size from 320 to 3,840 px on each side, as long as the long side is at most twice the short side, and up to 8 per device type — so an App Store compliance check does not carry over; our [Google Play screenshot requirements guide](/guides/google-play-screenshot-requirements) covers that store.',
      ],
    },
    {
      heading: 'How to fix a rejected screenshot and resubmit',
      body: [
        'Apple’s own help is clear on the key point: if your app was rejected for a metadata issue, you can resubmit the same build after resolving the issue. Screenshots can be uploaded while the version is in Prepare for Submission, Invalid Binary, Rejected, Metadata Rejected or Developer Rejected status.',
      ],
      bullets: [
        'Open the unresolved-issues link at the top of the app page in App Store Connect and read which guideline and which images the reviewer cited.',
        'If the fix is technical, re-export at an accepted size. For an alpha-channel error, converting the PNG to JPEG with the [free image converter](/image-converter) flattens transparency onto white and keeps the pixel size.',
        'If the fix is editorial, replace the flagged images in every localization and device size where they appear — the same image is often reused in several places.',
        'Resubmit the same build. If you think the reviewer misunderstood a screenshot, reply to App Review with an explanation instead (replies are limited to 4,000 characters and can include attachments).',
      ],
    },
    {
      heading: 'A pre-upload checklist that prevents most rejections',
      body: [
        'Once a version is approved, you must create a new version to change its screenshots, so it pays to get them right before submitting. For the design side — ordering, captions and testing — see our [App Store screenshot best practices](/guides/app-store-screenshot-best-practices). For compliance, check:',
      ],
      bullets: [
        'Every image is exactly one of the accepted sizes for its slot, in PNG or JPEG, with no alpha channel.',
        'One to 10 screenshots per device size and localization.',
        'Each screenshot shows the app in use — no splash, login or title-card-only frames.',
        'The App Store set uses Apple devices only; no Android frames, logos or store badges.',
        'No prices, discounts or time-limited offers in the images, and paid features are labelled as such.',
        'Every screen shown exists in the build under review, with no beta labels or debug UI.',
        'Content would pass a 4+ rating.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/iphone-6-9.jpg',
      alt: 'An App Store screenshot on a 3D iPhone mockup exported at 1320×2868 in Ecranify',
      caption: 'Exported with the iPhone 6.9″ preset (1320×2868), an accepted App Store size.',
    },
  ],
  faq: [
    {
      q: 'Why does App Store Connect say my screenshot is the wrong size?',
      a: 'The image does not match any accepted size for that display slot exactly. For the 6.9-inch iPhone slot the accepted portrait sizes are 1320 × 2868, 1290 × 2796 and 1260 × 2736 pixels. Re-export at one of them rather than resizing an existing file by hand.',
    },
    {
      q: 'Can App Store screenshots have a transparent background?',
      a: 'No. Apple states that screenshots can’t include alpha channels or transparencies. Export as JPEG, or flatten the image onto an opaque background before saving it as PNG.',
    },
    {
      q: 'Do I need a new build after a screenshot rejection?',
      a: 'No. If the rejection is about metadata, such as screenshots, you can upload corrected images and resubmit the same build.',
    },
    {
      q: 'Can I change App Store screenshots after my app is approved?',
      a: 'Not on the approved version. Apple requires you to create a new app version to update screenshots once a version has been approved.',
    },
    {
      q: 'Can App Store screenshots include text and device frames?',
      a: 'Yes. Guideline 2.3.3 allows text and image overlays, as long as the screenshot still shows the app in use. Use Apple device frames only, because imagery of other mobile platforms breaks guideline 2.3.10.',
    },
    {
      q: 'Can screenshots show features that require an in-app purchase?',
      a: 'Yes, but guideline 2.3.2 asks that screenshots clearly indicate whether featured items, levels or subscriptions require additional purchases. Label premium features in the caption so a reviewer and a customer can tell what is included.',
    },
  ],
  cta: {
    title: 'Export App Store screenshots at an accepted size',
    body: 'Build your screenshot on a 3D iPhone or iPad and export with a built-in App Store preset — 1320 × 2868 or 2064 × 2752. Free to start, pay only when you export.',
  },
}

// ── App Store icon requirements and guidelines ─────────────────────

const appStoreIconGuidelines: GuideData = {
  slug: 'app-store-icon-guidelines',
  canonicalPath: '/guides/app-store-icon-guidelines',
  title: 'App Store Icon Size & Guidelines (2026) | Ecranify',
  description:
    'App Store icon size and requirements for 2026: the 1024 × 1024 master, classic iPhone and iPad sizes, Liquid Glass, App Review rules and Google Play’s spec.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'App Store icon size, requirements and guidelines (2026)',
  intro: [
    'The App Store icon size is 1024 × 1024 px: one master image for iPhone, iPad and Mac that you add in Xcode, not in App Store Connect. Leave the corners square, because the system applies the rounded mask itself, and make sure the App Store icon has no transparency or alpha channel.',
    'On top of that, icons are now layered Liquid Glass objects with dark, clear and tinted appearances, and App Review has its own rules about what an icon may show. This guide covers the sizes for every Apple platform, the classic small iPhone and iPad sizes, the design and review rules, Google Play’s 512 × 512 icon specification, and how to export every size from one image.',
  ],
  updated: 'October 2026',
  readingTime: '9 min read',
  sections: [
    {
      heading: 'App Store icon sizes for every Apple platform',
      body: [
        'Apple’s Human Interface Guidelines define one master size per platform. You supply a square (a rectangle for Apple TV) and the system masks it into the final shape. The icon is only one of the files a listing needs; our [App Store assets checklist](/guides/app-store-assets) covers the rest for both stores.',
        'For iPhone and iPad, Xcode can generate every smaller icon variation from a single 1024 × 1024 image, and that is the default for new projects. The long list of small point sizes only matters if your project still uses a hand-filled asset catalog.',
      ],
      table: {
        caption: 'App icon sizes and shapes by platform (October 2026)',
        headers: ['Platform', 'Canvas size (px)', 'What you supply', 'Final shape'],
        rows: [
          ['iPhone, iPad, Mac', '1024 × 1024', 'Square, layered', 'Rounded rectangle'],
          ['Apple Watch', '1088 × 1088', 'Square, layered', 'Circle'],
          ['Apple Vision Pro', '1024 × 1024', 'Square, layered (3D)', 'Circle'],
          ['Apple TV', '800 × 480', 'Rectangle, 2–5 layers', 'Rounded rectangle'],
          ['Google Play store icon', '512 × 512', 'Full square, 32-bit PNG', 'Rounded corners (30% radius)'],
        ],
      },
    },
    {
      heading: 'Do you still need the small iPhone and iPad icon sizes?',
      body: [
        'Usually not. Apple’s Xcode documentation says iOS, iPadOS, tvOS and watchOS apps can generate every icon variation from a single 1024 × 1024 px image, and that this is the default for new projects. Supply individual sizes only if you want to customize a variant, for example to show more detail at a larger size.',
        'macOS is the exception: if you use a classic asset catalog instead of Icon Composer, Apple asks for an asset for each size. Older iOS projects with a filled-in icon set use the sizes below, taken from Apple’s archived icon size reference (Technical Q&A QA1686).',
      ],
      table: {
        caption: 'Classic iPhone and iPad app icon sizes',
        headers: ['Where it appears', 'Points', 'iPhone (px)', 'iPad (px)'],
        rows: [
          ['Home Screen', '60 pt (iPhone), 76 pt (iPad)', '120 × 120, 180 × 180', '76 × 76, 152 × 152'],
          ['Home Screen on iPad Pro', '83.5 pt', '—', '167 × 167'],
          ['Spotlight', '40 pt', '80 × 80, 120 × 120', '40 × 40, 80 × 80'],
          ['Settings', '29 pt', '58 × 58, 87 × 87', '29 × 29, 58 × 58'],
          ['App Store', '—', '1024 × 1024', '1024 × 1024'],
        ],
      },
    },
    {
      heading: 'Where the App Store icon comes from',
      body: [
        'There is no icon upload field in App Store Connect. You create the icon in Icon Composer or add it to an asset catalog in your Xcode project, and it reaches App Store Connect inside the build you upload.',
        'The practical consequence: changing the icon after release means creating a new app version, uploading a new build and submitting it for review. Screenshots work differently, because they are metadata — our [guide to rejected App Store screenshots](/guides/app-store-screenshot-rejection-reasons) covers how those are replaced.',
      ],
    },
    {
      heading: 'Liquid Glass: icon layers and six appearances',
      body: [
        'Since iOS 26, iPadOS 26, macOS Tahoe 26 and watchOS 26, home screen icons are built from layers of Liquid Glass. An icon is a background layer plus one or more foreground layers, and the system adds specular highlights, refraction and translucency on top.',
        'Apple’s tool for this is Icon Composer, which requires macOS Tahoe 26.4 or later. You draw the foreground layers in your usual design tool, import them, set a solid or gradient background, preview the result across appearances and export the icon for Xcode.',
        'On iPhone, iPad and Mac an icon has six appearances: default, dark, clear light, clear dark, tinted light and tinted dark. The system generates any variant you don’t provide. If you do provide a dark icon, give it a transparent background so the system background shows through, and keep the core shape the same in every appearance — swapping elements between variants makes the app harder to find when someone changes their Home Screen look.',
      ],
    },
    {
      heading: 'Design rules from Apple’s Human Interface Guidelines',
      body: ['Most of Apple’s icon guidance is about what to leave out, because the system now draws the effects:'],
      bullets: [
        'Supply square, unmasked layers. Pre-rounded corners or a baked-in circle weaken the specular highlights and make edges look jagged.',
        'Don’t add your own highlights, drop shadows, bevels, blurs or glows; the system applies dynamic versions of all of them.',
        'Keep the main subject centered so masking doesn’t cut it off, especially in the circular Apple Watch and Vision Pro icons.',
        'Prefer vector layers (SVG or PDF) with text converted to outlines, and PNG for raster art. Supported color spaces are sRGB, Gray Gamma 2.2 and Display P3.',
        'Use text only when it is essential to the brand. Apple specifically discourages words like “Watch”, “Play” or “New”, and the app name already appears under the icon.',
        'Prefer illustrations to photos, don’t replicate UI components, and never use replicas of Apple hardware.',
      ],
    },
    {
      heading: 'App Review rules and upload errors that affect icons',
      body: [
        'The most common technical failure happens before anyone reviews the app: the build is refused with an error saying the App Store icon in the asset catalog can’t be transparent nor contain an alpha channel. The fix is to re-export the 1024 × 1024 file with the alpha channel turned off and an opaque background behind every pixel.',
        'After that, App Review applies the guidelines. There is no dedicated icon section, but these rules name icons directly:',
      ],
      bullets: [
        'Guideline 2.3.8 — icons, like screenshots and previews, must suit a 4+ age rating even if the app is rated higher, and the small, large, Apple Watch and alternate icons should look similar.',
        'Guideline 4.1(c) — you can’t use another developer’s icon, brand or product name in your icon without their approval.',
        'Guideline 2.3.10 — no names, icons or imagery of other mobile platforms or alternative app marketplaces.',
      ],
    },
    {
      heading: 'Google Play icon requirements',
      body: [
        'Google Play’s store icon specification is short: 512 × 512 px, a 32-bit PNG in the sRGB color space, no larger than 1024 KB. Upload it as a full square. Google Play applies the rounded mask (a corner radius of 30% of the icon size) and the shadow itself, so don’t bake in either. Phone and tablet screenshots in the same listing have their own size and count rules, covered in our [Google Play screenshot requirements](/guides/google-play-screenshot-requirements).',
        'Google also rules out text or graphics that indicate a ranking, promote deals or incentivize installs, signal participation in a Google Play program, or could mislead users. For the larger banner at the top of a listing, see our [Google Play feature graphic guide](/guides/google-play-feature-graphic).',
        'The launcher icon inside the app is an adaptive icon: foreground and background layers of 108 × 108 dp, of which only the inner 66 × 66 dp is guaranteed to stay visible after masking. Keep the logo between 48 and 66 dp. Legacy launcher PNGs follow Android’s density buckets: 48 px at mdpi, 72 at hdpi, 96 at xhdpi, 144 at xxhdpi and 192 at xxxhdpi. Add a monochrome layer to support themed icons on Android 13 and later; from Android 16 QPR 2, the system themes apps that don’t provide one automatically.',
      ],
    },
    {
      heading: 'Export every icon size from one image',
      body: [
        'If you maintain a classic asset catalog or still ship Android density buckets, the [free Ecranify app icon generator](/icon-generator) turns one source image into the full set. Crop and zoom the image, pick a solid or gradient background, optionally add a label strip, and download one ZIP.',
        'The ZIP holds 18 iOS files in an AppIcon.appiconset folder, from the 20-point notification icon up to the 1024 × 1024 App Store icon, plus five Android launcher sizes from 48 to 192 px and the 512 × 512 Play Store icon. Every file is a full square with the background filled edge to edge, and everything is generated in your browser — nothing is uploaded.',
        'What it doesn’t do is build layered Icon Composer files. For Liquid Glass appearances you still import separate layers into Icon Composer. If Xcode reports an alpha channel in the 1024 × 1024 file, re-save it with alpha turned off before uploading.',
      ],
    },
    {
      heading: 'Test a new icon before you switch',
      body: [
        'Both stores let you test an icon on real store traffic. On the App Store, product page optimization runs up to three treatments against your original page for up to 90 days. Alternate icons must already be included in the binary of your published app, so an icon test needs an app version, while a test of screenshots alone does not.',
        'In Google Play Console, store listing experiments can include icon variants, with up to two variants against the current listing. You can run one default graphics experiment per app at a time, or up to five localized experiments. Test one asset at a time so the result is readable; the same tools work for screenshots, as covered in our [App Store screenshot best practices](/guides/app-store-screenshot-best-practices).',
      ],
    },
    {
      heading: 'Icon checklist before you submit',
      body: ['Run through this list before any release that changes the icon:'],
      bullets: [
        'A 1024 × 1024 master (1088 × 1088 for Apple Watch) with square, unmasked corners.',
        'No alpha channel in the App Store icon; transparency only in the dark variant’s background.',
        'Default, dark and tinted looks previewed in Icon Composer, with the same core shape in each.',
        'Google Play: 512 × 512 32-bit PNG under 1024 KB, square, no shadow, no ranking or promo badges.',
        'Adaptive launcher icon with the logo inside the 66 dp safe zone, plus a monochrome layer.',
      ],
    },
  ],
  screenshots: [],
  faq: [
    {
      q: 'What size is the App Store icon?',
      a: 'The master icon is 1024 × 1024 pixels for iPhone, iPad and Mac, and also for Apple Vision Pro. Apple Watch icons are 1088 × 1088 pixels and Apple TV icons are 800 × 480 pixels.',
    },
    {
      q: 'Should I round the corners of my app icon?',
      a: 'No. Apple and Google both apply the mask themselves. Supply a full square image; pre-rounded corners make the edges look jagged and, on Apple platforms, interfere with the Liquid Glass highlights.',
    },
    {
      q: 'Why does my App Store icon fail with an alpha channel error?',
      a: 'The 1024 × 1024 App Store icon can’t be transparent or contain an alpha channel. Re-export the file with alpha turned off and an opaque background.',
    },
    {
      q: 'What size is the Google Play icon?',
      a: 'Google Play needs a 512 × 512 pixel, 32-bit PNG in sRGB, no larger than 1024 KB, uploaded as a full square without rounded corners or a drop shadow.',
    },
    {
      q: 'Can I A/B test my app icon?',
      a: 'Yes. App Store product page optimization tests up to three treatments, and alternate icons must be included in your published binary. Google Play store listing experiments test up to two icon variants against the current listing.',
    },
    {
      q: 'Can I change my App Store icon without releasing a new version?',
      a: 'No. The icon ships inside the build, so changing it means creating a new app version, uploading a new build and submitting it for App Review.',
    },
  ],
  cta: {
    title: 'Your icon is ready. Now the screenshots.',
    body: 'Put your app on a 3D iPhone, iPad or Pixel and export App Store and Google Play screenshots at the exact store sizes. Free to start, pay only when you export.',
  },
}

// ── Google Play screenshot requirements ─────────────────────────────

const playScreenshotRequirements: GuideData = {
  slug: 'google-play-screenshot-requirements',
  canonicalPath: '/guides/google-play-screenshot-requirements',
  title: 'Google Play Screenshot Size & Requirements (2026) | Ecranify',
  description:
    'Google Play screenshot size for 2026: 1080 × 1920 px for phones, the 320–3,840 px limits, tablet, Wear OS, TV, Automotive and XR specs, and the content rules.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'Google Play screenshot size and requirements (2026)',
  intro: [
    'The recommended Google Play screenshot size for phones is 1080 × 1920 px in portrait or 1920 × 1080 px in landscape. Any JPEG or 24-bit PNG without an alpha channel is accepted if each side is between 320 px and 3,840 px and the long side is no more than twice the short side.',
    'You need at least two screenshots to publish and can add up to eight per device type. Apps need at least four phone screenshots at 1080 px or more to be eligible for Google Play’s recommendation formats; games need at least three.',
    'The rest is where listings go wrong: separate slots for 7-inch and 10-inch tablets and Chromebooks, rules for Wear OS, TV, Android Automotive and Android XR, and content guidelines about text, badges and device imagery. This guide covers each one, with a checklist to run before you upload. For the App Store side, see our [App Store and Google Play screenshot sizes guide](/guides/app-store-screenshot-sizes).',
  ],
  updated: 'November 2026',
  readingTime: '8 min read',
  sections: [
    {
      heading: 'Google Play screenshot size and requirements at a glance',
      body: [
        'These rules apply to every screenshot you upload in Play Console, whatever device type it is for. Inside the limits, Google does not fix one exact pixel size, so 1080 × 1920 px is a recommendation rather than a mandate:',
      ],
      table: {
        caption: 'General Google Play screenshot rules (November 2026)',
        headers: ['Rule', 'Requirement'],
        rows: [
          ['File format', 'JPEG or 24-bit PNG, no alpha channel'],
          ['Shortest side', 'At least 320 px'],
          ['Longest side', 'At most 3,840 px'],
          ['Proportion', 'Long side no more than twice the short side'],
          ['Recommended phone size', '1080 × 1920 px portrait or 1920 × 1080 px landscape'],
          ['Minimum to publish', '2 screenshots'],
          ['Maximum', '8 per supported device type'],
        ],
      },
    },
    {
      heading: 'Google Play screenshot dimensions by device type',
      body: [
        'Each device type has its own slot in the store listing, and the screenshots best suited to the shopper’s device are the ones Google Play shows. For phones, Google asks for at least four screenshots with a minimum resolution of 1080 px if you want the app to be eligible for recommendations: 1080 × 1920 px for portrait or 1920 × 1080 px for landscape.',
        'Tablets have two separate slots in Play Console, 7-inch and 10-inch, next to a Chromebook slot. All three follow the same 16:9 or 9:16 ratios. Google’s help page gives them a range of 1,080 to 7,680 px per side, while the general rule above caps any side at 3,840 px, so a file between 1,080 and 3,840 px satisfies both.',
      ],
      table: {
        caption: 'Google Play screenshot specs by device type (November 2026)',
        headers: ['Device type', 'How many', 'Aspect ratio', 'Size'],
        rows: [
          ['Phone', 'At least 4 recommended, up to 8', '9:16 or 16:9', '1080 × 1920 or 1920 × 1080 px recommended'],
          ['7-inch tablet', 'At least 4, up to 8', '9:16 or 16:9', '1,080–7,680 px per side'],
          ['10-inch tablet', 'At least 4, up to 8', '9:16 or 16:9', '1,080–7,680 px per side'],
          ['Chromebook', 'At least 4, up to 8', '9:16 or 16:9', '1,080–7,680 px per side'],
          ['Wear OS', 'At least 1', '1:1', 'At least 384 × 384 px'],
          ['Android TV', 'At least 1, plus a TV banner', '16:9 banner', 'Banner 1280 × 720 px'],
          [
            'Android Automotive OS',
            '2 portrait + 2 landscape',
            'Portrait and landscape',
            '800 × 1280 and 1024 × 768 px',
          ],
          ['Android XR', '4–8', '8:5', '3840 × 2400 px recommended, 1920 × 1200 minimum, up to 8 MB'],
        ],
      },
    },
    {
      heading: 'Google Play screenshot rules for games',
      body: [
        'Games get a slightly different recommendation bar. To be eligible for Google Play’s recommendation formats that use screenshots, a game needs at least three 16:9 landscape screenshots at 1920 × 1080 px or more, or three 9:16 portrait screenshots at 1080 × 1920 px or more. Apps need four.',
        'The file rules do not change for games: the same formats, the same 320 to 3,840 px limits and the same eight-per-device-type maximum apply. Pick one orientation for the set that matches how the game is played, because the requirement counts three screenshots in the same ratio.',
      ],
    },
    {
      heading: 'Rules for Wear OS, TV, Automotive and XR',
      body: [
        'The other form factors have stricter content rules than phones. They only apply if your app supports that form factor:',
      ],
      bullets: [
        'Wear OS: show only the app interface. Google rules out device frames, text, extra graphics and transparent backgrounds. Watch faces follow the same 1:1 rule and should show more than one variant if the face is customizable.',
        'Android TV: at least one screenshot before you publish, plus a 1280 × 720 px TV banner as a JPEG or 24-bit PNG.',
        'Android Automotive OS: screenshots are required for non-parked apps and optional but recommended for parked apps. They must depict the app accurately and can’t show specific vehicle models or a car maker’s own interface.',
        'Android XR: four to eight PNG or JPEG files at 8:5, which is a different shape from every phone and tablet slot, so plan a separate set.',
      ],
    },
    {
      heading: 'What Google’s screenshot content guidelines ask for',
      body: [
        'Beyond file specs, Google’s guidance for preview assets says screenshots should demonstrate the actual in-app or in-game experience. The practical rules:',
      ],
      bullets: [
        'Add taglines only when they are needed to explain what the app does, and keep them to no more than 20% of the image.',
        'Leave out rankings, awards and user testimonials, price or promotional words such as “Sale” or “Free”, and calls to action such as “Download now”.',
        'Avoid Google Play or other store badges, and third-party trademarked characters or logos you don’t have permission to use.',
        'Clean up the notification bar before capturing: no carrier names and no notifications.',
        'Write alt text for each screenshot, 140 characters or less, describing the important part of the image.',
        'Localize the text in your graphics for each market and language you list in.',
      ],
    },
    {
      heading: 'The Metadata policy: what gets a listing rejected',
      body: [
        'Separate from these guidelines is Google Play’s Metadata policy. It bans text or images in the app title, icon or developer name that indicate store performance or ranking, price or promotional information, or a relation to Google Play programs. Google’s examples include “#1”, “Best of Play”, “10% off” and “Editor’s choice”. The icon itself has its own size rules, covered in our [App Store and Google Play icon requirements guide](/guides/app-store-icon-guidelines).',
        'The policy’s examples also show misleading symbols, excessive special characters, and inappropriate imagery such as sexually suggestive content or graphic violence in promotional images.',
      ],
    },
    {
      heading: 'Device frames in Google Play screenshots',
      body: [
        'Google lists device imagery among the elements to avoid in screenshots, because it can become obsolete quickly or alienate some users. For Wear OS screenshots, device frames are ruled out entirely.',
        'Ecranify builds screenshots around 3D device mockups, so this matters for how you use it. If you follow Google’s guidance to the letter, upload full-screen UI captures with a short tagline. If you decide that a device scene sells your app better, use a current Android device rather than a dated one, keep the app’s screen as the largest element, and build the Play set on Android devices.',
      ],
    },
    {
      heading: 'Exporting Google Play screenshots in Ecranify',
      body: [
        'The [free Ecranify editor](/free) includes a Pixel 10 Pro XL and Google Play export presets: Phone (1080 × 1920), Tablet 10″ (1920 × 3413) and the 1024 × 500 feature graphic, which our [Google Play feature graphic guide](/guides/google-play-feature-graphic) covers in detail. The full editor adds the Pixel 10 Pro, Samsung Galaxy S26 Ultra, Xiaomi 17 and Galaxy Tab S11 Ultra.',
        'Every preset exports at exactly the store resolution, and all of them fall inside Google’s size limits. The 9:16 tablet export also fits the 7-inch tablet and Chromebook slots, since those share the same ratio and size range. The editor is free to use; you pay only when you export.',
        'Ecranify exports PNG files. If Play Console reports an alpha channel, converting the file to JPEG with the [free image converter](/image-converter) flattens any transparency onto white and keeps the pixel size.',
      ],
    },
    {
      heading: 'Google Play screenshot checklist',
      body: ['Run through this list before you upload a new set:'],
      bullets: [
        'Every file is a JPEG or 24-bit PNG with no alpha channel, with sides between 320 and 3,840 px and a ratio no longer than 2:1.',
        'At least four phone screenshots at 1080 × 1920 or 1920 × 1080 px (three for games), and no more than eight per device type.',
        '7-inch tablet, 10-inch tablet and Chromebook sets at 16:9 or 9:16 if the app supports large screens.',
        'Wear OS screenshots are 1:1, at least 384 × 384 px, with no frame, text or transparency.',
        'No rankings, awards, prices, promotions, calls to action or store badges, and taglines under 20% of the image.',
        'A clean notification bar, alt text for each image and localized text for each market.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/galaxy-tab.jpg',
      alt: 'Google Play tablet screenshot made in Ecranify on a Galaxy Tab',
      caption: 'Google Play 10″ tablet screenshot, exported at store size.',
    },
  ],
  faq: [
    {
      q: 'How many screenshots does Google Play require?',
      a: 'You need at least two screenshots to publish a store listing, and you can add up to eight per supported device type. For phone apps, Google recommends at least four screenshots at 1080 px or more to be eligible for recommendations.',
    },
    {
      q: 'What size should Google Play screenshots be?',
      a: 'Each side must be between 320 and 3,840 pixels, and the long side can be no more than twice the short side. For phones, 1080 × 1920 pixels in portrait or 1920 × 1080 pixels in landscape is the recommended size.',
    },
    {
      q: 'Do games have different Google Play screenshot requirements?',
      a: 'The file rules are the same. To be eligible for recommendations, games need at least three screenshots in one ratio: 16:9 landscape at 1920 × 1080 pixels or more, or 9:16 portrait at 1080 × 1920 pixels or more. Apps need at least four.',
    },
    {
      q: 'Can Google Play screenshots have a transparent background?',
      a: 'No. Screenshots must be JPEG or 24-bit PNG files without an alpha channel. Wear OS screenshots also must not use transparent backgrounds.',
    },
    {
      q: 'Are device frames allowed in Google Play screenshots?',
      a: 'Google advises against device imagery in screenshots because it can become obsolete quickly or alienate some users. For Wear OS screenshots, device frames are not allowed at all.',
    },
    {
      q: 'Can I add text to Google Play screenshots?',
      a: 'Yes, but only when it is needed to explain the app. Google recommends that taglines take up no more than 20% of the image and contain no calls to action, rankings or promotional language.',
    },
  ],
  cta: {
    title: 'Make your Google Play screenshots at the right size',
    body: 'Put your app on a 3D Pixel or Galaxy device and export Google Play screenshots and the feature graphic at exact store sizes. Free to start, pay only when you export.',
  },
}

const appStoreCreativeAssets: GuideData = {
  slug: 'app-store-header-search-results-assets',
  canonicalPath: '/guides/app-store-header-search-results-assets',
  title: 'App Store Header & Search Results Asset Sizes | Ecranify',
  description:
    'App Store creative assets for iOS 27: Header 3840 × 1646, Search Results 3:2 and the 5244 × 2950 Universal PNG. Sizes, formats, safe areas and content rules.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'App Store header and search results asset sizes (iOS 27)',
  intro: [
    'The App Store Header asset is 3840 × 1646 px (21:9) and the Search Results asset is 3:2, from 1920 × 1280 up to 3840 × 2560 px. Both accept JPEG or PNG. A single 5244 × 2950 px (16:9) PNG can fill both placements, and none of the images may contain an alpha channel.',
    'These creative assets show on iOS 27 and iPadOS 27 and later. This guide lists the exact specs from Apple’s App Store Connect Help, the approximate safe areas, the content rules, and a workflow for producing all three files from one design, including our free [creative assets generator](/app-store-creative-assets).',
  ],
  updated: 'October 2026',
  readingTime: '7 min read',
  sections: [
    {
      heading: 'Creative asset sizes and formats',
      body: [
        'Apple’s specification page lists two placements, each with an image and a video variant. The Universal asset appears in both the Header and the Search Results tables, which is why it works as a single file for both. For the icon, screenshots, previews and Google Play graphics, see our [App Store assets checklist](/guides/app-store-assets).',
      ],
      table: {
        caption: 'Creative asset image specs (App Store Connect Help, October 2026)',
        headers: ['Placement', 'Aspect ratio', 'Resolution', 'Formats'],
        rows: [
          ['Product page Header', '21:9', '3840 × 1646', 'JPEG, JPG or PNG'],
          ['Search Results', '3:2', '1920 × 1280 to 3840 × 2560', 'JPEG, JPG or PNG'],
          ['Universal (Header or Search Results)', '16:9', '5244 × 2950', 'PNG only'],
        ],
      },
    },
    {
      heading: 'Video versions',
      body: [
        'Both placements can also use a video instead of an image. Header videos are 3840 × 1646 px, Search Results videos use the same 3:2 range as the image, and both run 5 to 30 seconds at 30 or 60 fps in .mov, .m4v or .mp4.',
        'Videos loop continuously. In search results they are muted and cannot be unmuted, while on the product page they start muted and the viewer can turn sound on. Design the first frame so it still works as a poster.',
        'A looping video is easiest to build from motion that returns to where it started: a slow camera pan across a wide background, floating screenshots, or a slideshow that cycles through your screens with a fade, blur, push or scale transition. Keep the movement slow and the first frame clean, because search results play the clip muted and in a small crop.',
        'Rendering a video means encoding thousands of 4K frames. A 30-second clip at 60 fps is 1,800 frames and takes minutes on ordinary hardware, while 30 fps takes half the time and is a safe choice for a short, looping asset. The generator shows the remaining time and lets you cancel at any point.',
      ],
    },
    {
      heading: 'No alpha channel',
      body: [
        'Apple’s image restrictions state that images cannot include alpha channels or transparencies. A PNG exported from most design tools carries an alpha channel even when nothing in it is transparent, so flatten the file on an opaque background or export JPEG for the two non-Universal placements.',
        'The Universal asset is PNG only, so for that file the check matters most. Open the exported PNG in an image inspector and confirm it is 24-bit RGB rather than RGBA before uploading.',
      ],
    },
    {
      heading: 'Safe areas: where the crop falls',
      body: [
        'Header and Search Results crop the same artwork differently depending on device and orientation. Apple advises keeping the focal point in the centre of the composition so nothing important is clipped, and publishes Figma, Photoshop, Pixelmator and Sketch templates with the exact crops.',
        'Measurements taken from those templates by third parties put the central safe area at roughly the values below. Treat them as approximate and check them against the current Apple templates before you finalize.',
      ],
      table: {
        caption: 'Approximate safe areas, measured from Apple’s templates by third parties',
        headers: ['Asset', 'Canvas (px)', 'Approx. safe area (px)'],
        rows: [
          ['Header', '3840 × 1646', '1646 × 661, centred'],
          ['Search Results', '3840 × 2560', '2168 × 1030, centred'],
          ['Universal', '5244 × 2950', '1402 × 962, above centre'],
        ],
      },
    },
    {
      heading: 'Content rules',
      body: [
        'Creative assets go through App Review, and Apple’s guidance restricts what they may show. Keep text to a short phrase that supports the image rather than describing it.',
      ],
      bullets: [
        'No specific prices or discounts, website URLs or copyright symbols.',
        'No unverifiable claims, Apple badges such as Editor’s Choice or App of the Day, or logos of other platforms.',
        'No placeholder content, and nothing that misrepresents what the app does.',
        'The asset must suit a 4+ age rating even if your app is rated higher.',
        'Localize any text, and prefer artwork that works without words in smaller markets.',
      ],
    },
    {
      heading: 'What shows when you skip them',
      body: [
        'Creative assets are optional. Without a Search Results asset, search keeps showing your screenshots and app previews, so they are an addition to a strong screenshot set, not a replacement for it. Our [screenshot best practices](/guides/app-store-screenshot-best-practices) still decide most of your conversion.',
      ],
    },
    {
      heading: 'One design, three files',
      body: [
        'Design once around a centred focal point, then export each size. A 2:1 block of content sitting inside the smallest safe area survives every crop above, so lay out the headline, icon and one or two screenshots inside it and let only the background bleed to the edges.',
        'The [creative assets generator](/app-store-creative-assets) does this in the browser: pick a layout, add a background, key art, up to three screenshots, an icon and a headline, and it renders 3840 × 1646, 3840 × 2560 and 5244 × 2950 files as opaque images in one ZIP, or a seamless looping MP4 (camera pan, floating screenshots or a screenshot slideshow) for the Header and Search Results video slots. It shows an approximate safe-area overlay, and nothing is uploaded.',
        'If you still need the screenshots themselves, the [screenshot size guide](/guides/app-store-screenshot-sizes) lists every required resolution.',
      ],
    },
  ],
  screenshots: [],
  faq: [
    {
      q: 'What size is the App Store header image?',
      a: 'The product page Header image is 3840 × 1646 px at a 21:9 aspect ratio, as JPEG or PNG. A 5244 × 2950 px PNG (16:9) can also be used for it.',
    },
    {
      q: 'What size is the App Store search results asset?',
      a: 'The Search Results asset has a 3:2 aspect ratio, from 1920 × 1280 px to 3840 × 2560 px, as JPEG or PNG. The 5244 × 2950 px PNG can be used instead.',
    },
    {
      q: 'What is the Universal creative asset?',
      a: 'It is a single 5244 × 2950 px, 16:9 PNG that Apple lists under both Header and Search Results, so one upload can cover both placements.',
    },
    {
      q: 'Can creative assets have transparency?',
      a: 'No. Apple’s image restrictions say images cannot include alpha channels or transparencies. Export on an opaque background.',
    },
    {
      q: 'Which devices show creative assets?',
      a: 'They show on iOS 27 and iPadOS 27 and later.',
    },
    {
      q: 'Can I make a creative asset video without video editing software?',
      a: 'Yes. The free generator renders a looping MP4 from your design in the browser, with a background pan, floating or sliding content or a screenshot slideshow. Use 5 to 30 seconds at 30 or 60 fps, the range Apple lists for these videos.',
    },
    {
      q: 'Are header and search results assets required?',
      a: 'No, they are optional. Without them your screenshots and app previews keep appearing as before.',
    },
  ],
  cta: {
    title: 'Make all three assets in one go',
    body: 'Build the Header, Search Results and Universal files from one design, free in your browser. Then put your app on a 3D device for the screenshots.',
  },
  ctaSecondaryLinks: [{to: '/app-store-creative-assets', label: 'Free creative assets generator'}],
}

const panoramicScreenshots: GuideData = {
  slug: 'panoramic-app-store-screenshots',
  canonicalPath: '/guides/panoramic-app-store-screenshots',
  title: 'Panoramic App Store Screenshots: 2026 Size Guide | Ecranify',
  description:
    'Panoramic App Store screenshot sizes for 2026: slice and canvas dimensions, the gap between frames, how many frames to connect, iPad and Google Play rules.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'Panoramic App Store screenshots: sizes, slicing and design rules',
  intro: [
    'Panoramic App Store screenshots are one wide design — a background, a headline or a device — cut into several screenshots that line up edge to edge when someone swipes through the gallery. There is no special panorama size: each slice must be a normal accepted screenshot size, such as 1206 × 2622 px for the required iPhone slot, and the canvas is the slice width times the number of frames, so three iPhone slices need a 3618 × 2622 px canvas before any gap.',
    'You upload the slices in order, never the wide canvas. Both stores accept them: Google Play’s guidelines explicitly allow stylized screenshots that break UI across multiple images. This guide covers the slice sizes, how wide the canvas has to be, how many frames to connect, why iPad and Google Play need their own panorama, where the seams should fall, and how to build a set without slicing by hand.',
  ],
  updated: 'November 2026',
  readingTime: '9 min read',
  sections: [
    {
      heading: 'What makes a screenshot set panoramic',
      body: [
        'In a regular set every screenshot is its own card. In a panoramic (also called connected or continuous) set, elements cross the boundary between frames: a gradient flows from one image into the next, a phone sits half in frame 2 and half in frame 3, or a line of copy leads the eye to the right. The effect is a hint that there is more to see, which invites a swipe.',
        'The store does not know your set is panoramic. It shows separate images with its own spacing between them, so everything that makes the panorama work has to be planned on your side: exact slice widths, the gap the gallery adds, and an order you will not change without re-slicing.',
      ],
    },
    {
      heading: 'Panoramic screenshot sizes for each store',
      body: [
        'Every slice must be an accepted screenshot size on its own. The panorama is only the canvas you cut them from. These are the limits that matter, from Apple’s App Store Connect screenshot specifications and Google’s Play Console Help (checked November 2026). Apple accepts the same sizes in landscape with width and height swapped, and no screenshot may contain transparency.',
      ],
      table: {
        caption: 'Per-slice limits for panoramic screenshot sets (November 2026)',
        headers: ['Store / device', 'Slice size (portrait, px)', 'Max images', 'Formats'],
        rows: [
          [
            'App Store, iPhone with Dynamic Island (medium display) — required',
            '1206 × 2622 or 1179 × 2556',
            '10 per device size',
            'JPEG, JPG, PNG',
          ],
          [
            'App Store, iPhone with Dynamic Island (large display)',
            '1320 × 2868, 1290 × 2796 or 1260 × 2736',
            '10 per device size',
            'JPEG, JPG, PNG',
          ],
          [
            'App Store, iPad 13″ (if the app runs on iPad)',
            '2064 × 2752 or 2048 × 2732',
            '10 per device size',
            'JPEG, JPG, PNG',
          ],
          [
            'Google Play, any device type',
            '320–3840 px per side, long side ≤ 2× short side',
            '8 per device type',
            'JPEG or 24-bit PNG (no alpha)',
          ],
        ],
      },
    },
    {
      heading: 'How wide the panoramic canvas has to be',
      body: [
        'The canvas width is the slice width times the number of frames, plus any gap you leave between frames. With no gap, three 1206 px slices need a 3618 px canvas. Apple’s specifications do not publish the spacing the gallery puts between screenshots, so many designers leave an empty gutter of a few percent of the slice width; that strip is never exported, and the visible parts of a device that crosses it still line up once the store adds its own spacing.',
        'Every slice must come out at exactly the accepted size. A canvas that is a few pixels off produces a slice App Store Connect rejects, or, after rescaling, a seam where the background no longer meets. The full list of accepted variants is in our [App Store and Google Play screenshot sizes guide](/guides/app-store-screenshot-sizes).',
      ],
      table: {
        caption: 'Canvas width examples (portrait slices)',
        headers: ['Slices', 'Slice width (px)', 'Gap', 'Canvas width (px)'],
        rows: [
          ['3', '1206', 'none', '3618'],
          ['3', '1206', '5% (60 px × 2)', '3738'],
          ['5', '1320', 'none', '6600'],
          ['5', '1320', '5% (66 px × 4)', '6864'],
        ],
      },
    },
    {
      heading: 'How many frames a panorama should span',
      body: [
        'The ceiling is the store limit: up to 10 screenshots per device size on the App Store and up to 8 per device type on Google Play. Using all of them for one panorama is rarely a good idea. Apple says the first one to three screenshots appear in search results when no app preview is available, depending on orientation, and Google asks developers to prioritize real UI in the first three screenshots, so the frames people see most are the first few.',
        'Most connected sets therefore join two to five frames; the rest of the gallery can stay as separate cards. If your listing has an app preview, App Store search results lead with the video instead of your opening screenshots, so frame 1 of the panorama is no longer the first image a searcher sees, and it has to hold up on the product page alone.',
      ],
    },
    {
      heading: 'One panorama per device family',
      body: [
        'A panorama built for iPhone cannot simply be reused on iPad or Google Play, because the slices have different proportions. Stretching or cropping the same canvas moves every seam and distorts the devices that cross them. Plan a separate composition for each store size you upload.',
      ],
      table: {
        caption: 'Slice proportions by device family (portrait)',
        headers: ['Device family', 'Example slice (px)', 'Width : height', 'Three-slice canvas, no gap (px)'],
        rows: [
          ['iPhone, Dynamic Island (medium)', '1206 × 2622', 'about 0.46 : 1', '3618 × 2622'],
          ['iPhone, Dynamic Island (large)', '1320 × 2868', 'about 0.46 : 1', '3960 × 2868'],
          ['iPad 13″', '2064 × 2752', '0.75 : 1 (3:4)', '6192 × 2752'],
          ['Google Play phone', '1080 × 1920', '0.5625 : 1 (9:16)', '3240 × 1920'],
        ],
      },
      bullets: [
        'Within iPhone one panorama is usually enough: the large and medium Dynamic Island sizes share almost the same proportions, so one composition exports at both, and App Store Connect fills a device class you leave empty with scaled screenshots from a larger class.',
        'iPad is required if your app runs on iPad, and its 3:4 frame is much wider, so a crossing device needs a new position rather than a resize.',
        'Google Play recommends at least four screenshots of at least 1080 px for its recommendation formats, so a four-slice 9:16 panorama at 1080 × 1920 covers that in one design.',
      ],
    },
    {
      heading: 'Design rules for seams and slices',
      body: [
        'A panorama is only seen whole by people who swipe through every frame. Apple notes that the first one to three screenshots appear in search results when no app preview is available, depending on orientation, so the opening frames carry most of the work. Design each slice so it still makes sense on its own:',
      ],
      bullets: [
        'Put the main value proposition fully inside frame 1. Do not let the first headline or the hero device run off its right edge.',
        'Keep text away from the seams. A word split across two images is unreadable in search results, where frames appear small and apart.',
        'Let backgrounds, shapes and secondary devices cross the seams. They create the continuity without hiding information.',
        'Check every slice on its own at a small size. If one only makes sense next to its neighbour, rework it.',
        'Lock the order before slicing. Swapping two screenshots later breaks every connection on both sides.',
        'Upload the slices, not the wide canvas, and check that every file is opaque (JPEG, or PNG without an alpha channel).',
        'Repeat the rhythm, not the layout. One crossing element per seam is usually enough; more makes the gallery look cluttered.',
      ],
    },
    {
      heading: 'Panoramic screenshots on Google Play',
      body: [
        'Google’s Play Console Help explicitly allows stylized screenshots that break UI across multiple uploaded images, but asks developers to prioritize real UI in the first three screenshots. A panorama that is all artwork and no app interface works against that advice.',
        'For eligibility in Google Play’s recommendation formats, apps need at least four screenshots at a minimum of 1080 px, for example 9:16 portrait at 1080 × 1920 or larger. A four-slice panorama at 1080 × 1920 meets that. The other file rules are in our [Google Play screenshot requirements guide](/guides/google-play-screenshot-requirements).',
      ],
    },
    {
      heading: 'How to make panoramic screenshots in Ecranify',
      body: [
        'In the Ecranify editor a panorama is one scene. Turn on carousel export, choose 2 to 5 frames and the slice size, and the editor shows the frame boundaries over the canvas so you can place a 3D device or headline across a seam deliberately. Some Mac apps offer panoramic backgrounds as well; our [ButterKit alternative comparison](/alternatives/butterkit) compares one of them with Ecranify.',
        'The gap between frames is a slider (5% of the slice width by default, 0–15%). The gutter is shown as a darkened band in the editor and is left out of the export, so every frame still comes out at exactly the slice size. Text and 2D images that span several frames are drawn into each frame they cross.',
        'The export produces one image per frame, which you can download together as a ZIP and upload in order. Carousel export and custom sizes such as 1206 × 2622 are part of the full editor, where you pay per exported render; the free editor exports single screenshots at preset sizes. For the layout side of a set, see our [App Store screenshot best practices](/guides/app-store-screenshot-best-practices).',
        'Export size and carousel settings belong to each scene, so the iPad version of a panorama is a copy of the iPhone scene set to the iPad 13″ preset, with the device moved to suit the wider frames.',
        'Slicing by hand in a design tool also works: place one frame per slice side by side at the exact slice size, run the shared background behind all of them, and export each frame as its own image. Our [Figma App Store screenshots guide](/guides/figma-app-store-screenshots) covers the frame sizes and export settings for that route.',
      ],
    },
    {
      heading: 'Test a panorama before you commit to it',
      body: [
        'A connected set is not automatically better than separate cards. It costs more to change, and its benefit depends on how far people swipe. Both stores let you measure it: Product Page Optimization in App Store Connect tests alternative screenshot sets against your original, and store listing experiments in Play Console do the same for Google Play.',
        'Test one variable at a time: the panoramic set against the same screens as separate cards, with the same first-frame headline. If the panorama wins, the continuity helped; if not, the first frame was carrying the result anyway. Our [App Store screenshot A/B testing guide](/guides/app-store-screenshot-ab-testing) explains the limits of both tools and how long a test needs.',
      ],
    },
  ],
  screenshots: [
    {
      src: '/marketing/examples/carousel.jpg',
      alt: 'A three-frame panoramic App Store screenshot strip made in Ecranify',
      caption: 'One continuous background and device, sliced into store-size frames.',
      wide: true,
    },
    {
      src: '/marketing/examples/space-impact-carousel.jpg',
      alt: 'Two-frame connected screenshot where the phone continues from frame 1 into frame 2',
      caption: 'A device crossing the seam — frame 1 still works on its own.',
      wide: true,
    },
  ],
  faq: [
    {
      q: 'What is a panoramic App Store screenshot?',
      a: 'It is one wide design sliced into several screenshots that line up edge to edge in the store gallery, so a background, device or headline continues from one image into the next.',
    },
    {
      q: 'What size should each panoramic slice be?',
      a: 'Each slice must be an accepted screenshot size on its own, for example 1206 × 2622 px for the required iPhone medium display or 1320 × 2868 px for the large display. The canvas is the slice width times the number of frames, plus any gap.',
    },
    {
      q: 'How many screenshots can a panorama span?',
      a: 'The App Store accepts up to 10 screenshots per device size and Google Play up to 8 per device type, so a panorama can span at most that many frames. Most sets connect two to five, because the first few screenshots are the ones most people see.',
    },
    {
      q: 'Can I use the same panorama for iPhone and iPad?',
      a: 'Not without reworking it. iPhone slices are about 0.46 : 1 and iPad 13-inch slices are 3:4, so the same canvas would stretch or move every seam. Build a separate panorama for iPad, and another for Google Play if you use a different slice size there.',
    },
    {
      q: 'Does Google Play allow panoramic screenshots?',
      a: 'Yes. Play Console Help says stylized screenshots that break UI across multiple uploaded images are allowed, but recommends prioritizing app UI in the first three screenshots.',
    },
    {
      q: 'Do I need a gap between panoramic slices?',
      a: 'The stores add their own spacing between screenshots and do not publish its size. Leaving an empty gutter of a few percent of the slice width keeps a device that crosses a seam visually aligned.',
    },
  ],
  cta: {
    title: 'Build a panoramic set without slicing by hand',
    body: 'Compose the strip as one 3D scene, set the frames and the gap, and export every slice at the exact store size.',
  },
}

const appStoreAssets: GuideData = {
  slug: 'app-store-assets',
  canonicalPath: '/guides/app-store-assets',
  title: 'App Store Assets and Sizes: The 2026 Checklist | Ecranify',
  description:
    'Every App Store and Google Play listing asset in one checklist: icon, screenshots, app previews, iOS 27 creative assets, in-app event media and feature graphic.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'App Store assets: every image and video you need for iOS and Google Play',
  intro: [
    'The App Store assets you need for a listing are a 1024 × 1024 px app icon in your build and at least one iPhone screenshot at 1206 × 2622 or 1179 × 2556 px, plus a 13″ iPad screenshot if the app runs on iPad. Everything else is optional: app previews, the iOS 27 header and search results assets, and in-app event media.',
    'Google Play asks for more up front: a 512 × 512 px icon, a 1024 × 500 px feature graphic and at least two screenshots. This checklist lists every asset for both stores with the current sizes and formats from Apple’s and Google’s own help pages, and what each one is for.',
  ],
  updated: 'October 2026',
  readingTime: '7 min read',
  sections: [
    {
      heading: 'The App Store assets checklist',
      body: [
        'Apple splits listing media into three groups: the icon, which ships inside the app binary; screenshots and app previews, which belong to an app version; and the creative assets added for iOS 27, which can be approved on their own. The table below covers all of them for iPhone and iPad.',
      ],
      table: {
        caption: 'App Store assets for iPhone and iPad (Apple Developer and App Store Connect Help, October 2026)',
        headers: ['Asset', 'Needed', 'Size (px)', 'Format and limits'],
        rows: [
          [
            'App icon',
            'Required, added in Xcode',
            '1024 × 1024',
            'Square layers or a flat image; the system applies the rounded mask',
          ],
          [
            'iPhone screenshots',
            'Required: iPhone with Dynamic Island (medium display)',
            '1206 × 2622 or 1179 × 2556 portrait',
            '.jpeg, .jpg or .png, 1 to 10 per device size',
          ],
          [
            'iPad screenshots',
            'Required if the app runs on iPad',
            '2064 × 2752 or 2048 × 2732 portrait',
            '.jpeg, .jpg or .png, 1 to 10 per device size',
          ],
          [
            'App previews',
            'Optional',
            '886 × 1920 (iPhone), 1200 × 1600 (iPad 13″)',
            '.mov, .m4v or .mp4, 15–30 s, max 30 fps, 500 MB, up to 3',
          ],
          [
            'Product page header',
            'Optional, iOS 27 and later',
            '3840 × 1646 (21:9) or 5244 × 2950 PNG',
            'Image or 5–30 s looping video',
          ],
          [
            'Search results asset',
            'Optional, iOS 27 and later',
            '1920 × 1280 to 3840 × 2560 (3:2) or 5244 × 2950 PNG',
            'Image or 5–30 s looping video',
          ],
          [
            'In-app event card',
            'Required for each event',
            '1920 × 1080 to 3840 × 2160 (16:9)',
            'Image or 15–30 s video, landscape',
          ],
          [
            'In-app event details page',
            'Required for each event',
            '1080 × 1920 to 2160 × 3840 (9:16)',
            'Image or 15–30 s video, portrait',
          ],
        ],
      },
    },
    {
      heading: 'Google Play store listing assets',
      body: [
        'Google Play uploads every graphic in Play Console, including the icon, and three of them block publishing until they are there: the app icon, the feature graphic and at least two screenshots across your device types.',
      ],
      table: {
        caption: 'Google Play graphic assets (Play Console Help, October 2026)',
        headers: ['Asset', 'Needed', 'Size (px)', 'Format and limits'],
        rows: [
          ['App icon', 'Required', '512 × 512', '32-bit PNG with alpha, up to 1024 KB'],
          ['Feature graphic', 'Required', '1024 × 500', 'JPEG or 24-bit PNG, no alpha'],
          [
            'Phone screenshots',
            'At least 2 to publish; 4 at 1080 px or more for recommendations',
            '320 to 3,840 per side, long side at most 2× the short side; 1080 × 1920 recommended',
            'JPEG or 24-bit PNG, no alpha, up to 8',
          ],
          [
            'Tablet and Chromebook screenshots',
            'At least 4 for large screens',
            '1,080 to 7,680 per side, 16:9 or 9:16',
            'JPEG or 24-bit PNG, no alpha, up to 8',
          ],
          ['Preview video', 'Optional', 'YouTube video', 'Public or unlisted, ads off, not age-restricted'],
          ['TV banner', 'Required for Android TV apps', '1280 × 720', 'JPEG or 24-bit PNG, no alpha'],
        ],
      },
    },
    {
      heading: 'The minimum set to publish',
      body: [
        'If you only want to ship, these are the files neither store lets you skip. Everything else improves conversion and can follow later, and once the listing is live, [A/B testing your App Store screenshots](/guides/app-store-screenshot-ab-testing) shows which version of the set converts better.',
      ],
      bullets: [
        'App Store: the 1024 × 1024 icon in the build, one or more iPhone screenshots at 1206 × 2622 or 1179 × 2556, and 13″ iPad screenshots if the app runs on iPad.',
        'Apple only needs the highest required resolution when your UI is the same across sizes; App Store Connect scales those screenshots down for smaller devices.',
        'Google Play: the 512 × 512 icon, the 1024 × 500 feature graphic and at least two screenshots.',
        'Platform extras: Mac, Apple TV, Apple Vision Pro and Apple Watch apps each need their own screenshots, and Android TV apps need a TV screenshot plus the banner.',
      ],
    },
    {
      heading: 'App Store screenshots and app previews',
      body: [
        'Screenshots carry most of the listing. Apple allows up to 10 per device size, and depending on orientation up to three of them can appear in search results. The [App Store screenshot size guide](/guides/app-store-screenshot-sizes) lists every accepted resolution by device class, including the larger 6.9″ set many teams still export as well.',
        'App previews are short videos of real UI or gameplay. You can upload up to three per device size and language, 15 to 30 seconds each, and up to three can appear in search results. Apple’s guidance is to make them work silently first, because audio is muted by default in most places, and to pick a poster frame that stands on its own.',
        'One deadline to plan for: Apple lists separate screenshot sizes for iPhone Duo (outer display 1398 × 2034, inner display 2007 × 2853) and says they will be required from April 2027 for apps built with the iOS 27.1 SDK or later.',
      ],
    },
    {
      heading: 'iOS 27 creative assets and the Asset Library',
      body: [
        'Creative assets appear across the App Store in iOS 27 and iPadOS 27 and later: on the product page header, in search results and when your app is featured. They are optional. Without a search results asset, Apple shows your in-app events, app previews and screenshots instead. Our [header and search results asset guide](/guides/app-store-header-search-results-assets) covers the safe areas and the single 5244 × 2950 Universal file.',
        'In-app events need two pieces of media: a 16:9 landscape card and a 9:16 portrait details page. Apple adds a badge such as Live Event, Major Update or New Season on top, so design the artwork to complement the badge rather than repeat it.',
        'These images and videos now live in the Asset Library in App Store Connect. You can upload and submit them for review on their own, independent of an app version, so they are approved before a campaign starts. The Account Holder, Admin, App Manager and Marketing roles can manage them, and an asset can only be deleted while its status is Prepared for Submission.',
      ],
    },
    {
      heading: 'File formats and alpha channels',
      body: [
        'Transparency is the most common reason an upload fails. Apple’s screenshot and creative asset specifications both say images can’t include alpha channels or transparencies, and the Universal creative asset is PNG only, so flatten it on an opaque background.',
        'Google Play is stricter about format but has one exception. The feature graphic, the screenshots and the TV banner must be JPEG or 24-bit PNG without alpha, while the app icon is the only asset that is a 32-bit PNG with alpha. Our [Google Play feature graphic guide](/guides/google-play-feature-graphic) walks through the 1024 × 500 layout.',
      ],
    },
    {
      heading: 'Content rules that apply to every asset',
      body: [
        'Apple applies the same rules to every image and video it shows on the App Store, and App Review checks them. Google’s rules for screenshots are similar: show the real app, keep taglines to no more than 20% of the image, and use sharp, undistorted, correctly oriented images, as our [Google Play screenshot requirements](/guides/google-play-screenshot-requirements) explain in full.',
      ],
      bullets: [
        'Everything must suit a 4+ age rating, even if the app itself is rated higher.',
        'No specific prices or discounts, website URLs or copyright symbols.',
        'No logos of, or references to, other platforms or marketplaces.',
        'No Apple recognitions such as Editor’s Choice, App of the Day or Apple Design Award winner.',
        'Games must avoid gore, graphic imagery and weapons pointed directly at a person or the viewer.',
      ],
    },
    {
      heading: 'Making the whole set from one design',
      body: [
        'Most of the work is resizing the same idea for different slots, so start from one design and export each file at its exact size. In Ecranify you place your capture on a 3D device and export with presets for the iPhone 6.9″ (1320 × 2868) and iPad 13″ (2064 × 2752) slots, Google Play phone and tablet sizes and the 1024 × 500 feature graphic. For the required 6.3″ iPhone set, or event media such as 1920 × 1080 and 1080 × 1920, use a custom export size.',
        'Two free tools cover the rest in the browser: the icon generator exports the 1024 × 1024 App Store icon and the 512 × 512 Play Store icon in one ZIP, and the creative assets generator renders the Header, Search Results and Universal files as opaque images or a looping MP4.',
      ],
    },
  ],
  screenshots: [],
  faq: [
    {
      q: 'What assets do I need to publish on the App Store?',
      a: 'A 1024 × 1024 px app icon in your build and at least one screenshot for iPhone with Dynamic Island (medium display), at 1206 × 2622 or 1179 × 2556 px. If the app runs on iPad, you also need 13-inch iPad screenshots. App previews and creative assets are optional.',
    },
    {
      q: 'What graphic assets does Google Play require?',
      a: 'You must provide a 512 × 512 px app icon, a 1024 × 500 px feature graphic and at least two screenshots before you can publish a store listing. A preview video is optional.',
    },
    {
      q: 'Is the app icon uploaded in App Store Connect?',
      a: 'No. On the App Store the icon comes from your app build, which you set up in Xcode. On Google Play you upload the 512 × 512 icon in Play Console.',
    },
    {
      q: 'Can App Store assets have a transparent background?',
      a: 'No. Apple’s screenshot and creative asset specifications say images cannot include alpha channels or transparencies. On Google Play only the app icon may use alpha.',
    },
    {
      q: 'Can I update App Store images without releasing a new version?',
      a: 'Creative assets such as the header, search results and in-app event media can be uploaded and submitted in the Asset Library independent of an app version. Screenshots and app previews can be uploaded on a version page only while that version has a status such as Prepare for Submission or Rejected.',
    },
  ],
  cta: {
    title: 'Export every store size from one design',
    body: 'Put your app on a 3D iPhone, iPad or Pixel, then export the App Store and Google Play sizes you need, free to start in your browser.',
  },
  ctaSecondaryLinks: [
    {to: '/app-store-creative-assets', label: 'Free creative assets generator'},
    {to: '/icon-generator', label: 'Free app icon generator'},
  ],
}

const screenshotAbTesting: GuideData = {
  slug: 'app-store-screenshot-ab-testing',
  canonicalPath: '/guides/app-store-screenshot-ab-testing',
  title: 'App Store Screenshot A/B Testing Guide | Ecranify',
  description:
    'How to A/B test App Store and Google Play screenshots: Product Page Optimization and store listing experiments, what to test first, test length and results.',
  ogImage: '/og/ecranify-og.svg',
  eyebrow: 'Guide',
  h1: 'App Store screenshot A/B testing: how to run a test that gives an answer',
  intro: [
    'App Store screenshot A/B testing means showing part of your store visitors an alternative screenshot set and comparing how many of them download. On the App Store you do it with Product Page Optimization in App Store Connect; on Google Play with store listing experiments in Play Console. Both are built in and free to use.',
    'This guide covers the limits of each tool, what to test first, how to build variants that produce a readable result, how long a test needs and how to read the outcome. The design rules for the screenshots themselves are in our [App Store screenshot best practices](/guides/app-store-screenshot-best-practices).',
  ],
  updated: 'October 2026',
  readingTime: '7 min read',
  sections: [
    {
      heading: 'How screenshot A/B testing works on each store',
      body: [
        'Both tools split traffic at random: a share of visitors sees a variant instead of your current page, and the store counts conversions for each group. In App Store Connect you set a traffic proportion that is split evenly among treatments, so 30% with three treatments gives each one 10%. Play Console splits its audience percentage equally across variants.',
        'Apple shows treatments only on iOS 15 and iPadOS 15 or later, and product page optimization is not available for custom product pages or for App Store product pages on Apple Watch and iMessage. These are the limits from App Store Connect Help and Play Console Help (October 2026):',
      ],
      table: {
        caption: 'Screenshot A/B testing limits on the App Store and Google Play (October 2026)',
        headers: ['Setting', 'App Store (Product Page Optimization)', 'Google Play (store listing experiments)'],
        rows: [
          [
            'Variants per test',
            'Up to 3 treatments against the original page',
            'Up to 2 variants against the current listing',
          ],
          [
            'Tests at a time',
            'One test',
            'One default graphics experiment; up to 5 localized experiments, each with up to 5 languages',
          ],
          [
            'What you can test',
            'App icon, screenshots, app previews',
            'Icon, feature graphic, screenshots; descriptions in localized experiments',
          ],
          ['Maximum length', '90 days, or until you stop it', 'Stops automatically after 6 months'],
          [
            'Before it starts',
            'New metadata approved by App Review; alternate icons in the app binary',
            'Published app, tested against the current version',
          ],
          [
            'When a result counts',
            'Better or worse than the baseline with at least 90% confidence',
            'Difference above your minimum detectable effect at your chosen confidence level',
          ],
        ],
      },
    },
    {
      heading: 'What to test first',
      body: [
        'Each test should answer one question. Apple advises considering how many elements you change in a treatment so you can tell which one caused the result, and Play Console Help recommends testing one asset at a time. Start where most visitors look:',
      ],
      bullets: [
        'The first screenshot. When there is no app preview, Apple shows the first one to three screenshots in search results, so frame 1 is seen by people who never open your page.',
        'The headline on frame 1: one clear benefit against a list of features.',
        'The message that leads. Apple names showcasing a certain character or value proposition as one of the things a test can show works better.',
        'The order: the same images with a different opener.',
        'Device presentation: plain UI, a flat device frame or an angled 3D device.',
        'Connected against separate frames, using the same screens in both. Our [panoramic screenshots guide](/guides/panoramic-app-store-screenshots) explains how to build the connected version.',
        'Seasonal or localized content. Apple lists seasonal content and culturally relevant content for a specific localization as changes that can lead to more downloads.',
      ],
    },
    {
      heading: 'How to build screenshot variants that give a clear result',
      body: [
        'A treatment is a complete screenshot set. In App Store Connect you upload treatment screenshots per device size, and any device size you leave unedited uses the screenshots of your original product page. A test that changes only the iPhone set still shows your original iPad screenshots to iPad visitors.',
        'Keep everything except the tested variable identical: the same app screens, order, background and export size. A treatment that changes the caption and the device angle at once can win, but you will not know which change did it.',
        'Make the difference big enough to measure. Apple notes that a large lift needs less time to detect than a small one, and suggests testing a more dramatic change when a test is likely to be inconclusive. Treatment screenshots follow the same rules as your main set, so check them against the [App Store and Google Play screenshot sizes](/guides/app-store-screenshot-sizes) before uploading.',
      ],
    },
    {
      heading: 'Setting up the test in App Store Connect and Play Console',
      body: [
        'App Store: in App Store Connect, open the app, choose Product Page Optimization and create a test. Set the traffic proportion and the localizations (all are selected by default), then configure each treatment. The app must be live with the status Ready for Distribution or Pre-Order Ready for Distribution. New screenshots in a treatment must be approved by App Review before the test can start; tests without alternate icons can be submitted without a new app version, while alternate icons must be in the binary of the current version. Releasing a new version during a test can affect the results if it changes the tested assets.',
        'Google Play: store listing experiments run on published apps, on the default store listing or a custom store listing. Choose a default graphics experiment (default language only; it excludes users who see localized graphics) or a localized experiment, pick the target metric, such as unique user install clicks, and set the audience, minimum detectable effect and confidence level. Play Console then estimates how long the experiment needs.',
      ],
    },
    {
      heading: 'How long a screenshot test needs to run',
      body: [
        'There is no fixed minimum. App Store Connect shows results once at least five first-time downloads are attributed to the test and updates them daily, but Apple advises against ending a test early: wait until at least one treatment is declared better or worse than the baseline with at least 90% confidence. A test runs for up to 90 days, and App Store Connect can estimate the duration from your daily impressions and new downloads for the improvement you want to detect.',
        'Traffic is the main constraint. Apple notes that more treatments and the localizations you select can make a test take longer, so a small app reaches a result sooner with one treatment and a high traffic proportion. On Google Play an experiment can report “More data needed” and stops automatically after six months.',
      ],
    },
    {
      heading: 'Reading the results and applying the winner',
      body: [
        'App Store Connect labels each treatment Performing Better, Performing Worse, Collecting Data or Likely to be Inconclusive, next to its estimated conversion rate, improvement over the baseline and confidence. Apple’s analysis is Bayesian and combines test data with existing data about your product page. Applying a treatment copies its screenshots and app previews to your product page and ends the test; it cannot be undone, and an icon is not changed this way — you set the winning icon as the default in your next app version.',
        'Play Console reports a winner, a draw when the difference is below your minimum detectable effect, or “More data needed”. You can keep the current listing if no variant wins.',
        'A test that finds no winner is still a result: keep the original and test a bigger change next. Write down each hypothesis and outcome so the next test starts from what you learned.',
      ],
    },
    {
      heading: 'Building test variants in Ecranify',
      body: [
        'In the full Ecranify editor a project holds several scenes, one per screenshot. Add Scene copies the current scene, so a variant starts as an exact duplicate: change only the caption, the device angle or the background, and every other setting stays the same. Batch export renders all scenes and downloads them as a ZIP, ready to upload as a treatment set. If you would rather have an AI propose the variants, our [AppScreenshotStudio alternative](/alternatives/appscreenshotstudio) page compares a generator that drafts up to three test variants per set.',
        'Exports in the full editor are paid per render with credit packs; the free editor exports single screenshots at preset sizes. To try a layout before you build the variants, start in the [free editor](/free).',
      ],
    },
  ],
  screenshots: [],
  faq: [
    {
      q: 'Can you A/B test App Store screenshots?',
      a: 'Yes. Product Page Optimization in App Store Connect tests up to three treatments with alternate screenshots, app previews or app icons against your original product page, for up to 90 days.',
    },
    {
      q: 'How long should a screenshot A/B test run?',
      a: 'Until at least one treatment is declared better or worse than the baseline with at least 90% confidence, which Apple recommends waiting for. App Store tests run for up to 90 days; Google Play experiments stop automatically after six months.',
    },
    {
      q: 'Do screenshot test treatments need App Review?',
      a: 'On the App Store, yes: new metadata in a treatment must be approved by App Review before the test can start. Tests without alternate app icons can be submitted without a new app version.',
    },
    {
      q: 'How many screenshot variants can I test on Google Play?',
      a: 'Up to two variants against the current listing per experiment. Each app can have one default graphics experiment at a time and up to five localized experiments.',
    },
    {
      q: 'Can I A/B test a custom product page?',
      a: 'No. Apple says product page optimization is not available for custom product pages or for product pages on the App Store for Apple Watch and iMessage.',
    },
  ],
  cta: {
    title: 'Build every test variant from one scene',
    body: 'Duplicate a scene, change one thing, and export each variant at the exact App Store or Google Play size.',
  },
}

export const GUIDES = {
  appStoreSizes,
  playFeatureGraphic,
  figmaWorkflow,
  howToMake,
  bestPractices,
  foldableScreenshots,
  appleSeptember2026,
  localizedScreenshots,
  screenshotCaseStudy,
  freeTools,
  screenshotRejections,
  appStoreIconGuidelines,
  playScreenshotRequirements,
  appStoreCreativeAssets,
  panoramicScreenshots,
  appStoreAssets,
  screenshotAbTesting,
} as const
